// FabScore v2 — /pages/fabscore-v2
//
// A pure renderer over docs/fabscore/v2-spec.md's server-driven design
// (FR-3: the server decides which screen and what's on it; the client only
// renders blocks). There is no per-screen hardcoded UI logic here beyond a
// block-type registry — GET /screen returns a fully hydrated block list
// (question text, options, scores where relevant, score/grade, next-steps
// copy) and this file's only job is turning that JSON into DOM and posting
// back what she does.
//
// No build step — plain ES5-ish, self-contained IIFE, same convention as
// v1's assets/fabscore.js. Loaded directly by sections/fabscore-v2.liquid.
//
// Unknown block types are skipped, not thrown on (FR-9) — a newer backend
// must never break an older cached theme bundle.

(function () {
  'use strict';

  // ─── CONFIG ─────────────────────────────────────────────────
  var fb = window.__fabscoreV2 || {};
  var CONFIG = {
    apiBase: fb.apiBase || 'https://webserver.healthfab.in',
    shopDomain: fb.shopDomain || '',
    logoUrl: fb.logoUrl || '',
    landingChecklistIcons: fb.landingChecklistIcons || {},
    landingHeroPhotoUrl: fb.landingHeroPhotoUrl || '',
    landingStepBadgeUrl: fb.landingStepBadgeUrl || '',
    landingHowBgUrl: fb.landingHowBgUrl || '',
    landingFaqIcons: fb.landingFaqIcons || {},
    landingWalkawayIcons: fb.landingWalkawayIcons || {},
  };
  function apiUrl(path) { return CONFIG.apiBase.replace(/\/$/, '') + path; }

  var TOKEN_KEY = 'hfv2_token';
  function getStoredToken() { try { return localStorage.getItem(TOKEN_KEY); } catch (e) { return null; } }
  function setStoredToken(t) { try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch (e) { /* private mode etc. */ } }

  // ─── IDENTITY / ATTRIBUTION ─────────────────────────────────
  // anonId — generated once, kept in localStorage, sent on request-otp,
  // verify-otp and every /events batch. Stitches her pre-login events (the
  // ones fired before she has a phone number at all) onto her account once
  // she verifies. Per the backend contract this must survive across visits,
  // so localStorage (not sessionStorage) — a new tab/session should still
  // resolve to the same anonymous visitor.
  var ANON_ID_KEY = 'hfv2_anon_id';
  function getAnonId() {
    try {
      var v = localStorage.getItem(ANON_ID_KEY);
      if (!v) { v = 'a_' + Date.now().toString(36) + Math.random().toString(36).slice(2); localStorage.setItem(ANON_ID_KEY, v); }
      return v;
    } catch (e) { return 'a_' + Date.now().toString(36); }
  }
  var ANON_ID = getAnonId();

  // entryRef (?ref=FS_… on the WhatsApp link) — captured on first load,
  // because by the time onboarding/commit actually fires she may have spent
  // minutes on the page and the query string is the only place this ever
  // shows up. sessionStorage (not localStorage): it's specific to how she
  // arrived THIS visit, not a permanent identity like anonId/fabscoreRef.
  var ENTRY_REF_KEY = 'hfv2_entry_ref';
  (function captureEntryRef() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('ref');
      if (fromUrl) sessionStorage.setItem(ENTRY_REF_KEY, fromUrl);
    } catch (e) { /* private mode etc. */ }
  })();
  function getEntryRef() {
    try { return sessionStorage.getItem(ENTRY_REF_KEY) || null; } catch (e) { return null; }
  }

  // Her name and date of birth. The server knows both (fabscoreV2User.name /
  // .dob) but does not put them in the /screen envelope yet, so until it
  // does they're picked up as she answers them during onboarding and kept
  // here. The moment /screen carries a `profile`, that wins — see
  // currentProfile().
  var PROFILE_KEY = 'hfv2_profile';
  function readCachedProfile() {
    try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null') || {}; } catch (e) { return {}; }
  }
  function cacheProfile(patch) {
    try {
      var next = Object.assign(readCachedProfile(), patch);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
    } catch (e) { /* private mode — the panel just shows less */ }
  }
  function clearCachedProfile() { try { localStorage.removeItem(PROFILE_KEY); } catch (e) { } }

  // The onboarding screens the server has already issued, kept so Back has
  // something to go back TO. It survives a reload on purpose: GET /screen
  // hands back the earliest step with an unanswered required question, so
  // after a refresh she lands straight on step 2 or 3 and the client would
  // otherwise have no idea step 1 ever existed — Back would silently never
  // appear. sessionStorage (not local) because it belongs to this sitting.
  var SCREEN_STACK_KEY = 'hfv2_screen_stack';
  var storedStackVersion = null;
  function loadStoredScreenStack() {
    try {
      var parsed = JSON.parse(sessionStorage.getItem(SCREEN_STACK_KEY) || 'null');
      if (!parsed || !Array.isArray(parsed.stack)) return [];
      storedStackVersion = parsed.v != null ? parsed.v : null;
      return parsed.stack;
    } catch (e) { return []; }
  }
  function saveScreenStack(stack, version) {
    try {
      sessionStorage.setItem(SCREEN_STACK_KEY, JSON.stringify({ v: version, stack: stack }));
    } catch (e) { /* private mode, quota — Back just falls back to this session */ }
  }
  function clearScreenStack() {
    storedStackVersion = null;
    try { sessionStorage.removeItem(SCREEN_STACK_KEY); } catch (e) { }
  }

  // fabscoreRef — minted once she completes onboarding, then stable for her
  // whole journey (the backend contract says so explicitly: don't
  // regenerate it). It's what ties a Shopify order back to this journey, so
  // it has to survive as long as she might still add something to cart —
  // localStorage, updated from onboarding/commit's response AND from every
  // GET /screen response that carries one (both are documented sources).
  var FABSCORE_REF_KEY = 'hfv2_fabscore_ref';
  function getFabscoreRef() { try { return localStorage.getItem(FABSCORE_REF_KEY) || null; } catch (e) { return null; } }
  function setFabscoreRef(ref) { try { if (ref) localStorage.setItem(FABSCORE_REF_KEY, ref); } catch (e) { /* private mode etc. */ } }

  // ─── API LAYER ──────────────────────────────────────────────
  // Parses the backend's canonical envelope: { ok:true, data } / { ok:false, error:{code,message} }.
  var api = {
    _token: getStoredToken(),

    _headers: function () {
      var h = { 'Content-Type': 'application/json', 'x-shopify-shop-domain': CONFIG.shopDomain };
      if (this._token) h['Authorization'] = 'Bearer ' + this._token;
      return h;
    },

    _fetch: function (method, path, body) {
      var self = this;
      var opts = { method: method, headers: this._headers(), credentials: 'include' };
      if (body) opts.body = JSON.stringify(body);
      return fetch(apiUrl(path), opts).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok) {
            var e = json.error || {};
            return { ok: false, status: res.status, code: e.code || (res.status === 401 ? 'AUTH_REQUIRED' : 'SERVER_ERROR'), message: e.message || 'Something went wrong', details: e.details || null };
          }
          return { ok: true, status: res.status, data: json.data };
        });
      }).catch(function () {
        return { ok: false, status: 0, code: 'NETWORK_ERROR', message: 'You appear to be offline. Check your connection.' };
      });
    },

    getConfig: function () { return this._fetch('GET', '/api/fabscore/v2/auth/config'); },
    requestOtp: function (phone) { return this._fetch('POST', '/api/fabscore/v2/auth/request-otp', { phone: phone, shopDomain: CONFIG.shopDomain, anonId: ANON_ID }); },
    verifyOtp: function (phone, otp) {
      var self = this;
      return this._fetch('POST', '/api/fabscore/v2/auth/verify-otp', { phone: phone, otp: otp, shopDomain: CONFIG.shopDomain, anonId: ANON_ID })
        .then(function (res) {
          if (res.ok && res.data && res.data.token) { self._token = res.data.token; setStoredToken(res.data.token); }
          return res;
        });
    },
    getSession: function () { return this._fetch('GET', '/api/fabscore/v2/auth/session'); },
    logout: function () {
      var self = this;
      return this._fetch('POST', '/api/fabscore/v2/auth/logout', {}).then(function (res) {
        self._token = null; setStoredToken(null);
        clearScreenStack(); // her steps must not be replayable by whoever logs in next
        return res;
      });
    },
    // stepId is optional and only honoured by the server while onboarding is
    // incomplete — it's what lets Back ask for a step she has already
    // finished, which the bare call can never return (it always answers with
    // the earliest step holding an unanswered required question).
    getScreen: function (stepId) {
      var path = '/api/fabscore/v2/screen';
      if (stepId) path += '?stepId=' + encodeURIComponent(stepId);
      return this._fetch('GET', path);
    },
    answer: function (payload) { return this._fetch('POST', '/api/fabscore/v2/answer', payload); },
    commitOnboarding: function (payload) { return this._fetch('POST', '/api/fabscore/v2/onboarding/commit', payload || {}); },
    commitCheckin: function () { return this._fetch('POST', '/api/fabscore/v2/checkin/commit', {}); },
    // Public — no session required, but sent with whatever auth we have
    // anyway (harmless; the backend ignores it here). Never awaited by a
    // caller — see flushEvents(), this is fire-and-forget.
    sendEvents: function (body) { return this._fetch('POST', '/api/fabscore/v2/events', body); },
  };

  // ─── EVENTS ─────────────────────────────────────────────────
  // POST /events — batched (≤50/call), best-effort. sessionId is fresh per
  // page load (NOT persisted — anonId is the durable id, sessionId is only
  // here to group one visit's events together). contentVersion starts at 1
  // (matches "content pack v1 (seeded)") and is corrected from the first
  // real GET /screen response, which echoes the backend's actual value.
  var SESSION_ID = 's_' + Date.now().toString(36) + Math.random().toString(36).slice(2);
  var contentVersion = 1;
  var eventQueue = [];

  // `screen` on each queued event mirrors what she's actually looking at:
  // the tab if it's a static one (calendar/doctors/pcos), 'score' when
  // she's on the cached Score tab, otherwise whatever screen.id the server
  // last resolved for Home (an onboarding step, or score once nothing else
  // is pending). `state`/`COMING_SOON` are defined later in this file but
  // that's fine — this only ever runs after the whole IIFE has finished
  // defining everything, in response to a later event.
  function currentScreenId() {
    if (!state.loggedIn) return 'login';
    if (COMING_SOON[state.activeTab]) return state.activeTab;
    if (state.activeTab === 'score') return 'score';
    return (state.screen && state.screen.screen && state.screen.screen.id) || null;
  }

  function evTrack(name, props, flushNow) {
    eventQueue.push({ name: name, props: props || {}, screen: currentScreenId(), ts: Date.now() });
    if (flushNow || eventQueue.length >= 50) flushEvents();
  }

  // `useBeacon`: pagehide/visibilitychange can't wait on a normal fetch —
  // the page may already be gone by the time it would resolve. sendBeacon
  // fires-and-detaches instead; it can't carry custom headers, but /events
  // is a public endpoint so that's fine here specifically.
  function flushEvents(useBeacon) {
    if (!eventQueue.length) return;
    var batch = eventQueue.splice(0, eventQueue.length);
    var body = { anonId: ANON_ID, shopDomain: CONFIG.shopDomain, sessionId: SESSION_ID, contentVersion: contentVersion, events: batch };
    if (useBeacon && navigator.sendBeacon) {
      try { navigator.sendBeacon(apiUrl('/api/fabscore/v2/events'), new Blob([JSON.stringify(body)], { type: 'application/json' })); } catch (e) { /* best-effort */ }
      return;
    }
    api.sendEvents(body).catch(function () { /* best-effort — analytics must never break a screen */ });
  }

  setInterval(function () { flushEvents(); }, 5000);
  window.addEventListener('pagehide', function () { flushEvents(true); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flushEvents(true); });

  // Fired once, right after landing — force-flushed immediately (not on the
  // usual 5s/pagehide schedule) because a WhatsApp in-app WebView can close
  // within ~2 seconds of opening the link, before the next batch would ever
  // go out.
  var _sessionStartedSent = false;
  function trackSessionStarted() {
    if (_sessionStartedSent) return;
    _sessionStartedSent = true;
    evTrack('Session_Started', { entry: getEntryRef() ? 'whatsapp' : 'direct' }, true);
  }

  // ─── DOM HELPERS ────────────────────────────────────────────
  var app = function () { return document.getElementById('fabscore-v2-app'); };

  function h(tag, attrs) {
    var el = document.createElement(tag);
    attrs = attrs || {};
    for (var k in attrs) {
      if (!attrs.hasOwnProperty(k) || attrs[k] == null) continue;
      if (k === 'className') el.className = attrs[k];
      else if (k === 'innerHTML') el.innerHTML = attrs[k];
      else if (k === 'style' && typeof attrs[k] === 'object') { for (var s in attrs[k]) el.style[s] = attrs[k][s]; }
      else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') el.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
      else if (k === 'checked' || k === 'disabled' || k === 'required') { if (attrs[k]) el.setAttribute(k, ''); }
      else el.setAttribute(k, attrs[k]);
    }
    for (var i = 2; i < arguments.length; i++) append(el, arguments[i]);
    return el;
  }

  // Normalises anything a caller passes as a child. Arrays recurse through
  // here rather than being appended item-by-item, so a string INSIDE an array
  // still becomes a text node — appendChild throws on a raw string, and
  // renderLegalBlock passes [' ', <a>].
  function append(el, child) {
    if (child == null) return;
    if (Array.isArray(child)) { child.forEach(function (c) { append(el, c); }); return; }
    if (typeof child === 'string') { el.appendChild(document.createTextNode(child)); return; }
    el.appendChild(child);
  }
  function svg(markup) { var span = document.createElement('span'); span.style.display = 'inline-flex'; span.innerHTML = markup; return span.firstElementChild; }

  // ─── STATE ──────────────────────────────────────────────────
  var state = {
    booted: false,
    loggedIn: false,
    otpEnabled: true,      // GET /auth/config — the ops kill switch for WhatsApp OTP login
    screen: null,          // last resolved /screen payload — whatever the server says "now" is
    scoreScreen: null,     // last /screen payload that carried a score_gauge block — cached so
                            // the Score tab still has something to show even when the canonical
                            // screen has since moved on (e.g. a check-in came due)
    profileOpen: false,    // the top bar's profile panel
    cartCount: 0,          // items in Shopify's cart, read from /cart.js — never derived
    activeTab: 'home',     // 'home' | 'calendar' | 'score' | 'doctors' | 'pcos' — bottom-nav
                            // selection. Only 'home' and 'score' show server content; the other
                            // three are static, client-only "coming soon" screens (FR-9 / the
                            // product spec: no detection logic exists yet for any of them).
    error: null,
    saving: false,
    loginStep: 'phone',    // 'phone' | 'otp'
    loginPhone: '',
    devOtp: null,          // 6-digit code echoed by request-otp when the backend runs with FABSCORE_DEV_AUTH=true (never in production)
    faqOpenIndex: null,    // landing screen's FAQ accordion — index of the open item, or null
    // Onboarding screens the server has issued this session, oldest first.
    // GET /screen takes no arguments — it always answers with the earliest
    // step that still has an unanswered required question — so it cannot be
    // asked for a step she has already finished. Back therefore replays a
    // payload the server itself sent earlier; her answers come back
    // pre-filled from each question's savedAnswer. Forward is still the
    // server's call (loadScreen), never ours.
    screenStack: loadStoredScreenStack(),
    // period_calendar widget scratch state (only one such question in the whole flow)
    calMonthOffset: 0,     // months back from today for the single visible month; 0 = this month
    calPeriods: [],        // every recorded { start, end }, oldest first. A plain list, not
                            // slots per month — a period can run across a month boundary, and
                            // more than the required two are accepted and sent.
    calPendingStart: null, // a tapped start waiting for its end tap
  };

  function render() {
    var container = app();
    if (!container) return;
    container.hidden = false;
    container.style.display = 'block';
    container.innerHTML = '';

    var content;
    try {
      content = state.booted ? renderApp() : renderLoading();
    } catch (err) {
      console.error('[FabScoreV2] render failed', err);
      content = h('div', { className: 'hfv2-screen' }, h('div', { className: 'hfv2-card' },
        h('p', null, 'Something went wrong. Please refresh.'),
        h('button', { className: 'hfv2-cta', style: { marginTop: '12px' }, onClick: function () { location.reload(); } }, 'Refresh')));
    }
    container.appendChild(content);
    document.body.dataset.hfv2Screen = (state.screen && state.screen.screen && state.screen.screen.id) || (state.loggedIn ? '' : 'login');
    // Sync the age gate to whatever is on the freshly-built screen. Safe to
    // call unconditionally — it no-ops when there's no CTA or no age question.
    refreshCtaGate();
    trackScreenViewedIfChanged();
  }

  // The one hook every screen transition passes through. Guarded on
  // `currentScreenId()` actually changing, so a re-render caused by
  // something unrelated (an error banner appearing, saving/unsaving) never
  // re-fires Screen_Viewed for a screen she was already looking at.
  var _lastTrackedScreen = null;
  function trackScreenViewedIfChanged() {
    var id = currentScreenId();
    if (!id || id === _lastTrackedScreen) return;
    _lastTrackedScreen = id;
    evTrack('Screen_Viewed', {});
    if (id === 'onboarding_step_1') evTrack('Onboarding_Started', {});
    if (id === 'score') {
      var gauge = findBlockInScreen(state.scoreScreen, 'score_gauge');
      evTrack('Score_Viewed', { score: gauge ? gauge.score : null });
    }
  }

  function findBlockInScreen(payload, type) {
    var screen = payload && payload.screen;
    var blocks = (screen && screen.blocks) || [];
    for (var i = 0; i < blocks.length; i++) if (blocks[i].type === type) return blocks[i];
    return null;
  }

  function setState(partial) { Object.assign(state, partial); render(); }

  function renderLoading() {
    return h('div', { className: 'hfv2-loading' },
      h('div', { className: 'hfv2-loading__icon' }, svg('<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#A91B54" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>')),
      h('p', { className: 'hfv2-loading__text' }, 'Loading your health…'));
  }

  // Server first, local fallback second. `profile` on the /screen envelope
  // is the eventual source; nothing sends it today.
  function currentProfile() {
    var fromServer = (state.screen && state.screen.profile) || null;
    var cached = readCachedProfile();
    return {
      name: (fromServer && fromServer.name) || cached.name || null,
      dob: (fromServer && fromServer.dob) || cached.dob || null,
    };
  }

  function ageFromDob(dob) {
    if (!dob) return null;
    var p = String(dob).split('-');
    if (p.length !== 3) return null;
    var born = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    if (isNaN(born.getTime())) return null;
    var now = new Date();
    var years = now.getFullYear() - born.getFullYear();
    // Not a birthday-passed-yet check on the year alone: a December birthday
    // read in January is a year younger than the subtraction suggests.
    var monthDiff = now.getMonth() - born.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) years--;
    return years >= 0 && years < 130 ? years : null;
  }

  // Fixed to the top of every logged-in screen, the way the cart button is —
  // she can reach her account from wherever she happens to be, including
  // mid-onboarding.
  function renderTopBar() {
    var bar = h('div', { className: 'hfv2-topbar' },
      CONFIG.logoUrl
        ? h('img', { className: 'hfv2-topbar__logo', src: CONFIG.logoUrl, alt: 'Healthfab' })
        : h('span'), // keeps the account controls pinned right when there's no logo
      h('div', { className: 'hfv2-topbar__actions' },
        renderCartButton(),
        h('button', {
          className: 'hfv2-avatar', type: 'button',
          'aria-label': 'Your account', 'aria-expanded': state.profileOpen ? 'true' : 'false',
          onClick: function () { setState({ profileOpen: !state.profileOpen }); },
        }, svg('<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg>'))));
    if (state.profileOpen) bar.appendChild(renderProfilePanel());
    return bar;
  }

  // Horizon's own cart button can't be moved into this bar — its
  // `on:click="/open"` is resolved by walking up to the closest
  // <cart-drawer-component>, so lifting it out stops the drawer opening. It
  // is hidden on this page (see sections/fabscore-v2.liquid) and replaced by
  // the button below, which lives in the bar as an ordinary flex child and so
  // needs no positioning of its own. Tapping it calls the drawer component's
  // public open() — the same thing its own trigger does — so she still gets
  // the site's real cart, not a second one.
  function openThemeCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // The count is read from Shopify, never guessed at, and refreshed whenever
  // the theme announces a change — including from the drawer itself, so
  // removing something there is reflected here too.
  function refreshCartCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        if (!cart) return;
        if (cart.item_count !== state.cartCount) setState({ cartCount: cart.item_count });
      });
  }
  document.addEventListener('cart:update', refreshCartCount);

  function renderCartButton() {
    var btn = h('button', {
      className: 'hfv2-cart', type: 'button', 'aria-label': 'Your cart',
      onClick: function () { openThemeCartDrawer(); },
    }, svg('<svg width="19" height="19" viewBox="0 0 24 24" fill="none">'
      // Horizon's own cart mark (snippets/cart-icon.liquid), so this reads as
      // the same button it replaces. Its stroke is hardcoded navy there;
      // currentColor here, since this one sits on a pink fill.
      + '<path d="M18.7047 9.76855L19.9828 18.0762C20.1548 19.1946 19.2896 20.2031 18.1581 20.2031H5.84655C4.71507 20.2031 3.84981 19.1946 4.02186 18.0762L5.29997 9.76855C5.43853 8.86794 6.21345 8.20312 7.12466 8.20312H16.88C17.7912 8.20312 18.5661 8.86794 18.7047 9.76855Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '<path d="M15.6932 8.20328V7.2802C15.6932 5.24099 14.0402 3.58789 12.0009 3.58789C9.96164 3.58789 8.30859 5.24099 8.30859 7.2802V8.20328" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>'));
    if (state.cartCount > 0) {
      btn.appendChild(h('span', { className: 'hfv2-cart__count' }, String(state.cartCount)));
    }
    return btn;
  }

  function renderProfilePanel() {
    var profile = currentProfile();
    var age = ageFromDob(profile.dob);
    var panel = h('div', { className: 'hfv2-profile' });

    panel.appendChild(h('p', { className: 'hfv2-profile__name' }, profile.name || 'Your account'));
    // Only stated when it's actually known — a blank "Age —" row reads as
    // something having gone wrong.
    if (age != null) panel.appendChild(h('p', { className: 'hfv2-profile__meta' }, age + ' years old'));

    panel.appendChild(h('button', {
      className: 'hfv2-profile__logout', type: 'button',
      onClick: function () {
        api.logout().then(resetToLoggedOut, resetToLoggedOut);
      },
    }, 'Log out'));
    return panel;
  }

  // Everything that belonged to the session she just left. The calendar's
  // scratch state matters most: it lives outside `screen`, so without this
  // the next person to log in on the same device opens step 2 with her
  // periods already on it. `saving` is in here because a logout mid-write
  // would otherwise leave the next screen's button disabled.
  function resetToLoggedOut() {
    clearCachedProfile();
    clearScreenStack();
    answerChains = {};
    pendingAnswers.length = 0;
    setState({
      profileOpen: false, loggedIn: false, screen: null, scoreScreen: null,
      screenStack: [], activeTab: 'home', loginStep: 'phone', loginPhone: '',
      devOtp: null, error: null, saving: false,
      calPeriods: [], calPendingStart: null, calMonthOffset: 0,
    });
  }

  function renderApp() {
    if (!state.loggedIn) return renderLogin();
    // The top bar belongs to the app, not to any one screen, so it is
    // wrapped around whatever screen is current rather than being a block a
    // screen has to remember to include.
    return h('div', null, renderTopBar(), currentScreenNode());
  }

  function currentScreenNode() {
    // Calendar / Doctors / PCOS are pure client-side placeholders — nothing
    // is fetched for them, there is no server screen behind this tab yet.
    if (COMING_SOON[state.activeTab]) return renderComingSoonScreen(state.activeTab);

    if (state.activeTab === 'score') {
      // No score yet (onboarding not finished) — nothing cached to show.
      return state.scoreScreen ? renderResolvedScreen(state.scoreScreen) : renderScorePending();
    }

    // 'home' — whatever the server currently says is the primary screen:
    // an onboarding step, a due check-in, or (once nothing else is pending)
    // the score screen itself.
    if (!state.screen) return renderLoading();
    return renderResolvedScreen(state.screen);
  }

  // ── Coming soon (Calendar / Doctors / PCOS) ─────────────────
  var COMING_SOON = {
    calendar: { title: 'Calendar', text: "Cycle predictions from your check-ins are on the way — we'll let you know the moment it's ready." },
    doctors: { title: 'Doctors', text: "Talk to a doctor about your results, right from FabScore — coming soon." },
    pcos: { title: 'PCOS', text: 'A dedicated PCOS tracking mode is on the way — coming soon.' },
  };

  function renderComingSoonScreen(kind) {
    var cfg = COMING_SOON[kind] || {};
    var wrap = h('div', { className: 'hfv2-screen' });
    wrap.appendChild(h('div', { className: 'hfv2-pane-header' }, h('h1', { className: 'hfv2-pane-header__title' }, cfg.title || '')));
    wrap.appendChild(h('div', { className: 'hfv2-coming-soon' },
      h('span', { className: 'hfv2-coming-soon__badge' }, 'Coming soon'),
      h('h2', null, cfg.title),
      h('p', null, cfg.text)));
    // No backend screen exists for these tabs, so there's no bottom_nav
    // block to render it from — call the same renderer directly so it stays
    // the one place nav items/behaviour are defined.
    wrap.appendChild(renderBottomNavBlock());
    return wrap;
  }

  function renderScorePending() {
    var wrap = h('div', { className: 'hfv2-screen' });
    wrap.appendChild(h('div', { className: 'hfv2-pane-header' }, h('h1', { className: 'hfv2-pane-header__title' }, 'Score')));
    wrap.appendChild(h('div', { className: 'hfv2-checkin-idle' }, h('p', null, 'Finish your check-in to see your FabScore.')));
    wrap.appendChild(renderBottomNavBlock());
    return wrap;
  }

  function renderErrorBanner(onRetry) {
    if (!state.error) return null;
    return h('div', { className: 'hfv2-error' }, state.error, onRetry ? h('button', { onClick: onRetry }, 'Try again') : null);
  }

  // ─── LOGIN ──────────────────────────────────────────────────
  function digitsOnly(s) { return (s || '').replace(/\D/g, ''); }

  // Landing = login: this is the very first thing she sees, and the phone
  // field below is the real login form — there is no separate "welcome, tap
  // to continue" screen in front of it. Marketing content (walkaway/how-it-
  // works/features/testimonials/FAQ) renders below the same card, on both
  // login steps, so it reads as one continuous page rather than a screen
  // that gets replaced.
  // Each point gets its own exported icon (lightbulb / gauge / winking face) —
  // not a repeated checkmark — per the Figma hero checklist. URLs come from
  // the section's CONFIG.landingChecklistIcons (theme asset_url), matching
  // how the hero photo and logo are wired in from Liquid.
  var LANDING_CHECKLIST = [
    { icon: 'symptoms', text: 'Understand your symptoms' },
    { icon: 'score', text: 'Get your Health FabScore' },
    { icon: 'health', text: 'Improve your Health' },
  ];
  // Lines are hard-split (not left to wrap naturally) so the break lands in
  // the same place as Figma's on every device — natural wrapping at this
  // column's ~100px width was putting "Plan to improve your health" on 3
  // lines instead of the design's 2 ("Plan to improve" / "your health").
  // gap is icon-to-text spacing, per item — Figma (572:3513) top-aligns all
  // three icons (align-items: start on the row) rather than centering them,
  // and the gauge's icon is shorter (25.082px) than search/clipboard's
  // (29px), so it uses a taller gap (12px vs 9px/8px) to compensate — that's
  // what keeps the three text labels landing on the same line, not equal
  // gaps plus vertically-centered icons.
  var LANDING_WALKAWAY = [
    { icon: 'gauge', lines: ['Your Health', 'FabScore'], gap: 12 },
    { icon: 'search', lines: ['Identify your', 'problems'], gap: 9 },
    { icon: 'clipboard', lines: ['Plan to improve', 'your health'], gap: 8 },
  ];
  var LANDING_STEPS = [
    { title: 'Sign in', text: 'Use your mobile number & OTP on whatsapp' },
    { title: 'Take a 2-minute assessment', text: 'Help us understand your health' },
    { title: 'Get your FabScore & next steps', text: 'A detailed plan to improve your health' },
  ];
  var LANDING_FEATURES = [
    { icon: 'tag', label: 'Free' },
    { icon: 'lock', label: 'Private' },
    { icon: 'clock', label: '2 min' },
    { icon: 'exit', label: 'Leave anytime' },
  ];
  // All five stars render filled on every card, per the design — there is no
  // per-review rating in the source data, just a uniform 5-star display.
  var LANDING_TESTIMONIALS = [
    { name: 'Anita Shah', quote: "I liked that it considers how you feel, not just when your period starts." },
    { name: 'Sneha Reddy', quote: "I liked that it didn't diagnose me. It gave me a useful starting point." },
    { name: 'Harshita Jha', quote: 'I just took supplements randomly. FabScore made me think more about what I might actually need.' },
    { name: 'Vanita Jain', quote: 'I usually ignore bad sleep and mood changes, but FabScore made me notice the pattern.' },
    { name: 'Divya Agarwal', quote: "I knew iron was important, but I didn't know heavy periods could put you at risk of deficiency." },
  ];
  // The FAQ reuses the tag/clock shapes from the feature strip, but those are
  // stroked white for the pink strip — on the FAQ's white rows they'd be
  // invisible, so those two have their own pink variants (all five FAQ
  // icons below are #FF63A9 — the brand book's Hot Pink, sampled directly
  // off the Figma icon assets too; they'd drifted to the darker accent
  // magenta #A91B54 used for buttons/links elsewhere in the app).
  var LANDING_FAQ = [
    { icon: 'tag-pink', q: 'Is it really free?', a: "Yes — no card, no trial, no paywall to see your score. It's part of being a Healthfab customer." },
    { icon: 'phone', q: 'Do I need an account?', a: 'No. Your phone number is used once to save your score to you — nothing else, no password to set.' },
    { icon: 'document', q: 'What do I actually get?', a: 'A score, and next steps to do — the first time and every check-in after.' },
    { icon: 'heart', q: 'Is this medical advice?', a: "No. It's guidance and it flags when a symptom is worth taking to a doctor." },
    { icon: 'clock-pink', q: 'How long does it take?', a: 'About two minutes to answer questions and get your FabScore.' },
  ];
  // Small inline-SVG set for the landing sections — kept local to this
  // function's neighborhood rather than in the shared `svg()` caller sites,
  // since these are landing-only and not part of the block registry below.
  var LANDING_ICONS = {
    check: '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
    // Walkaway icons are the one place the landing uses colour beyond the
    // brand pink. The gauge used to be the exported Figma raster (a
    // reference screenshot with "68" and a caption baked in, masked with a
    // white patch + a live "95" text on top) — but that asset's background
    // is opaque white, not transparent, so it showed as a visible white box
    // against this section's pink-tinted background instead of blending in.
    // Rebuilt as a plain SVG instead: it's flat vector geometry (three arc
    // segments + a dot), not an illustration, so recreating it exactly is
    // legitimate rather than a redraw — and it's naturally transparent.
    // Arc colours sampled directly from the Figma export's pixels
    // (#CF3C3C / #F8B11C / #10A00E), not guessed. Dot sits at the
    // amber/green boundary, matching where Figma's own reference image has
    // it — not recalculated to "point at" 95, since Figma's doesn't either.
    gauge: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 61" width="41" height="25" fill="none" aria-hidden="true">'
      + '<path d="M8 55A42 42 0 0 1 30.28 17.92" stroke="#CF3C3C" stroke-width="11" stroke-linecap="round"/>'
      + '<path d="M30.28 17.92A42 42 0 0 1 73.49 20.18" stroke="#F8B11C" stroke-width="11" stroke-linecap="round"/>'
      + '<path d="M73.49 20.18A42 42 0 0 1 92 55" stroke="#10A00E" stroke-width="11" stroke-linecap="round"/>'
      + '<circle cx="73.49" cy="20.18" r="6" fill="#000"/>'
      + '<text x="50" y="52" text-anchor="middle" font-family="Montserrat, sans-serif" font-weight="700" font-size="26" fill="#000">95</text></svg>',
    search: '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1C1117" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.7-4.7"/><path d="M8 10.5h5M10.5 8v5" stroke="#A91B54"/></svg>',
    clipboard: '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#1E9E5A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="4" width="14" height="17" rx="2.5"/><rect x="9" y="2" width="6" height="4" rx="1.4" fill="#1E9E5A" stroke="none"/><path d="M9 11h6M9 15h4" stroke="#1C1117"/></svg>',
    'chevron-sm': '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#6B5560" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>',
    // tag/lock/clock/exit below are the real akar-icons/boxicons/cuida glyphs
    // exported from Figma's feature strip (572:3579), not hand-drawn — those
    // read as visibly different shapes from the design (e.g. the tag had no
    // notch-hole, the lock was a plain padlock rather than a keyhole).
    tag: '<svg viewBox="0 0 24.1668 24.1668" width="20" height="20" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15.2847 22.427C15.0502 22.6615 14.7719 22.8476 14.4655 22.9746C14.1591 23.1015 13.8307 23.1668 13.4991 23.1668C13.1674 23.1668 12.8391 23.1015 12.5327 22.9746C12.2263 22.8476 11.9479 22.6615 11.7135 22.427L1.73967 12.4532C1.50501 12.2189 1.31889 11.9407 1.19196 11.6344C1.06503 11.3281 0.999801 10.9997 1 10.6682V3.52467C1 2.13167 2.13167 1 3.52467 1H10.6682C11.3367 1 11.9795 1.266 12.4532 1.73967L22.427 11.7135C22.6615 11.948 22.8476 12.2263 22.9746 12.5327C23.1015 12.8391 23.1668 13.1675 23.1668 13.4991C23.1668 13.8307 23.1015 14.1591 22.9746 14.4655C22.8476 14.7719 22.6615 15.0502 22.427 15.2847L15.2847 22.427Z"/><path d="M5.08301 5.08203L5.66634 5.66536" stroke-linecap="round"/></svg>',
    lock: '<svg viewBox="0 0 24 24" width="20" height="20" fill="#fff" stroke="none"><path d="M6 22H18C19.1 22 20 21.1 20 20V11C20 9.9 19.1 9 18 9H17V7C17 4.24 14.76 2 12 2C9.24 2 7 4.24 7 7V9H6C4.9 9 4 9.9 4 11V20C4 21.1 4.9 22 6 22ZM9 7C9 5.35 10.35 4 12 4C13.65 4 15 5.35 15 7V9H9V7ZM6 11H18V20H13V17.72C13.59 17.37 14 16.73 14 16C14 14.9 13.1 14 12 14C10.9 14 10 14.9 10 16C10.0021 16.349 10.0955 16.6914 10.2709 16.9932C10.4464 17.2949 10.6977 17.5455 11 17.72V20H6V11Z"/></svg>',
    'lock-muted': '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#937D87" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
    clock: '<svg viewBox="0 0 22 22" width="20" height="20" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"/><path d="M14 15L11.586 12.586C11.2109 12.211 11.0001 11.7024 11 11.172V5"/></svg>',
    'tag-pink': '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FF63A9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4L12 22 2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z"/><circle cx="7" cy="7" r="1.3" fill="#FF63A9" stroke="none"/></svg>',
    'clock-pink': '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FF63A9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>',
    exit: '<svg viewBox="0 0 18.0006 20" width="18" height="20" fill="#fff" stroke="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0 5C0 3.67392 0.526784 2.40215 1.46447 1.46447C2.40215 0.526784 3.67392 0 5 0H10C10.2652 0 10.5196 0.105357 10.7071 0.292893C10.8946 0.48043 11 0.734784 11 1C11 1.26522 10.8946 1.51957 10.7071 1.70711C10.5196 1.89464 10.2652 2 10 2H5C4.20435 2 3.44129 2.31607 2.87868 2.87868C2.31607 3.44129 2 4.20435 2 5V15C2 15.7956 2.31607 16.5587 2.87868 17.1213C3.44129 17.6839 4.20435 18 5 18H10C10.2652 18 10.5196 18.1054 10.7071 18.2929C10.8946 18.4804 11 18.7348 11 19C11 19.2652 10.8946 19.5196 10.7071 19.7071C10.5196 19.8946 10.2652 20 10 20H5C3.67392 20 2.40215 19.4732 1.46447 18.5355C0.526784 17.5979 0 16.3261 0 15V5Z"/><path fill-rule="evenodd" clip-rule="evenodd" d="M11.47 5.31653C11.5598 5.22063 11.6676 5.14335 11.7873 5.08913C11.907 5.03491 12.0362 5.0048 12.1675 5.00053C12.2988 4.99626 12.4297 5.0179 12.5526 5.06423C12.6755 5.11056 12.7882 5.18066 12.884 5.27053L17.684 9.77053C17.7839 9.86407 17.8636 9.97713 17.918 10.1027C17.9725 10.2283 18.0006 10.3637 18.0006 10.5005C18.0006 10.6374 17.9725 10.7728 17.918 10.8984C17.8636 11.0239 17.7839 11.137 17.684 11.2305L12.884 15.7305C12.7881 15.8204 12.6755 15.8904 12.5526 15.9367C12.4296 15.983 12.2988 16.0047 12.1675 16.0004C12.0362 15.9961 11.907 15.966 11.7873 15.9118C11.6677 15.8576 11.5598 15.7804 11.47 15.6845C11.3802 15.5887 11.3101 15.4761 11.2638 15.3531C11.2175 15.2302 11.1959 15.0993 11.2002 14.968C11.2044 14.8367 11.2345 14.7075 11.2887 14.5879C11.3429 14.4682 11.4201 14.3604 11.516 14.2705L14.471 11.5005H5C4.73478 11.5005 4.48043 11.3952 4.29289 11.2076C4.10536 11.0201 4 10.7658 4 10.5005C4 10.2353 4.10536 9.98096 4.29289 9.79343C4.48043 9.60589 4.73478 9.50053 5 9.50053H14.471L11.516 6.73053C11.4201 6.64074 11.3428 6.53292 11.2886 6.41324C11.2344 6.29357 11.2043 6.16438 11.2 6.03307C11.1957 5.90175 11.2174 5.77088 11.2637 5.64793C11.31 5.52499 11.3801 5.41238 11.47 5.31653Z"/></svg>',
    'chevron-down': '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#12306B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>',
    phone: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FF63A9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="2" width="10" height="20" rx="2"/><path d="M11 18h2"/></svg>',
    document: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FF63A9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6M9 17h6"/></svg>',
    heart: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FF63A9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21.2l8.8-8.8a5.5 5.5 0 0 0 0-7.8z"/></svg>',
  };

  function renderLandingWalkaway() {
    return h('div', { className: 'hfv2-landing-section' },
      h('h2', { className: 'hfv2-landing-h2 hfv2-landing-h2--standalone' }, 'After the assessment you will get :-'),
      h('div', { className: 'hfv2-landing-walkaway-grid' },
        LANDING_WALKAWAY.map(function (item) {
          var iconUrl = CONFIG.landingWalkawayIcons && CONFIG.landingWalkawayIcons[item.icon];
          var icon;
          if (item.icon === 'gauge') {
            // Always the plain SVG (see LANDING_ICONS.gauge) — the exported
            // Figma raster for it has an opaque white background, so using
            // it showed as a visible white box against this section's
            // pink-tinted background instead of blending in.
            icon = svg(LANDING_ICONS.gauge);
          } else if (item.icon === 'search' && iconUrl) {
            // Figma crops this one (47.404x29 box, source scaled to 77.48%
            // width / 126.66% height) rather than showing it uncropped —
            // background-image + exact percentages reproduces that crop;
            // a plain <img src> would show the whole square icon instead.
            icon = h('span', { className: 'hfv2-landing-walkaway-icon hfv2-landing-walkaway-icon--search', style: { backgroundImage: 'url(' + iconUrl + ')' } });
          } else if (iconUrl) {
            icon = h('img', { className: 'hfv2-landing-walkaway-icon', src: iconUrl, alt: '' });
          } else {
            icon = svg(LANDING_ICONS[item.icon]);
          }
          return h('div', { className: 'hfv2-landing-walkaway-item' },
            icon,
            h('p', { style: { marginTop: item.gap + 'px' } }, item.lines.map(function (line) { return h('span', null, line); })));
        })));
  }

  function renderLandingHow() {
    // Figma's card is white with a faint (10%-opacity) wave-texture photo
    // behind the content, not a flat pink gradient — the gradient this used
    // to have read visibly more saturated/pink than the design.
    var bg = CONFIG.landingHowBgUrl
      ? h('div', { className: 'hfv2-landing-how-bg', style: { backgroundImage: 'url(' + CONFIG.landingHowBgUrl + ')' } })
      : null;
    return h('div', { className: 'hfv2-landing-how' },
      bg,
      h('div', { className: 'hfv2-landing-how-content' },
        h('h2', { className: 'hfv2-landing-h2' }, 'How FabScore works'),
        h('p', { className: 'hfv2-landing-sub' }, '3 steps, takes 2 minutes'),
        h('div', { className: 'hfv2-landing-steps' },
          LANDING_STEPS.map(function (step, i) {
            return h('div', { className: 'hfv2-landing-step' },
              h('span', {
                className: 'hfv2-landing-step-num',
                style: CONFIG.landingStepBadgeUrl ? { backgroundImage: 'url(' + CONFIG.landingStepBadgeUrl + ')' } : null,
              }, String(i + 1)),
              h('div', null,
                h('p', { className: 'hfv2-landing-step-title' }, step.title),
                h('p', { className: 'hfv2-landing-step-text' }, step.text)));
          }))));
  }

  function renderLandingFeatures() {
    return h('div', { className: 'hfv2-landing-features' },
      LANDING_FEATURES.map(function (f) {
        return h('div', { className: 'hfv2-landing-feature' }, svg(LANDING_ICONS[f.icon]), h('span', null, f.label));
      }));
  }

  function renderLandingTestimonials() {
    var track = h('div', { className: 'hfv2-landing-testimonial-track' },
      LANDING_TESTIMONIALS.map(function (t) {
        var stars = [];
        for (var i = 0; i < 5; i++) stars.push(h('span', { className: 'is-filled' }, '★'));
        return h('div', { className: 'hfv2-landing-testimonial-card' },
          h('div', { className: 'hfv2-landing-testimonial-stars' }, stars),
          h('p', { className: 'hfv2-landing-testimonial-quote' }, t.quote),
          h('p', { className: 'hfv2-landing-testimonial-name' }, t.name));
      }));
    return h('div', { className: 'hfv2-landing-section' },
      h('h2', { className: 'hfv2-landing-h2 hfv2-landing-h2--standalone' }, 'Hear from our users'),
      track);
  }

  function renderLandingFaq() {
    return h('div', { className: 'hfv2-landing-section' },
      h('h2', { className: 'hfv2-landing-h2' }, 'FAQ'),
      h('p', { className: 'hfv2-landing-sub' }, 'Everything you need to know'),
      h('div', { className: 'hfv2-landing-faq-list' },
        LANDING_FAQ.map(function (item, i) {
          var isOpen = state.faqOpenIndex === i;
          var row = h('div', { className: 'hfv2-landing-faq-item' + (isOpen ? ' is-open' : '') });
          row.appendChild(h('button', {
            type: 'button', className: 'hfv2-landing-faq-question',
            onClick: function () {
              var willOpen = !row.classList.contains('is-open');
              // Direct DOM toggle, not setState — setState rebuilds every
              // FAQ row from scratch, so the answer would just snap open
              // with nothing for a CSS transition to animate between (the
              // "before" state never coexists with the "after" state in the
              // DOM). Mutating state without re-rendering keeps a later,
              // unrelated setState still aware of which item is open.
              state.faqOpenIndex = willOpen ? i : null;
              document.querySelectorAll('.hfv2-landing-faq-item.is-open').forEach(function (el) {
                if (el !== row) el.classList.remove('is-open');
              });
              row.classList.toggle('is-open', willOpen);
            },
          },
            (function () {
              // Real exported Figma icons (tag/₹0, phone/check, doc/clock,
              // stethoscope/info, clock/bolt) — the hand-drawn generic
              // shapes used before (plain tag, plain phone, …) read as
              // noticeably different glyphs, not just a color mismatch.
              var iconUrl = CONFIG.landingFaqIcons && CONFIG.landingFaqIcons[item.icon];
              return h('span', { className: 'hfv2-landing-faq-icon' },
                iconUrl ? h('img', { src: iconUrl, alt: '', width: 20, height: 20 }) : svg(LANDING_ICONS[item.icon]));
            })(),
            h('span', { className: 'hfv2-landing-faq-question-text' }, item.q),
            h('span', { className: 'hfv2-landing-faq-chevron' }, svg(LANDING_ICONS['chevron-down']))));
          // Always in the DOM (not conditional on isOpen) — the grid-rows
          // collapse trick in CSS needs the element present to animate;
          // appending it only when open (the old behaviour) is what made it
          // pop in/out with no transition at all.
          // The padding lives on the innermost <p>, not on .hfv2-landing-faq-answer
          // itself — a grid item's own padding still renders even when the
          // 0fr track collapses its content height to 0 (confirmed live: it
          // was sitting at exactly 14px, its padding-bottom value, with
          // nothing else explaining that number). Nesting the padding one
          // level deeper, inside the overflow:hidden box, lets it get
          // clipped away along with the text instead of surviving the collapse.
          row.appendChild(h('div', { className: 'hfv2-landing-faq-answer-wrap' },
            h('div', { className: 'hfv2-landing-faq-answer' },
              h('p', { className: 'hfv2-landing-faq-answer-text' }, item.a))));
          return row;
        }),
        h('div', { className: 'hfv2-landing-faq-divider' })));
  }

  // Sticky CTA scrolls back to the phone field and focuses it — it is not
  // its own OTP request, since the real form lives in the hero card above.
  function renderLandingStickyCta() {
    return h('div', { className: 'hfv2-landing-sticky' },
      h('button', {
        type: 'button', className: 'hfv2-cta',
        onClick: function () {
          var hero = document.getElementById('hfv2-landing-hero');
          if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
          var input = document.getElementById('hfv2-phone');
          if (input) setTimeout(function () { input.focus(); }, 350);
        },
      }, 'Check my FabScore'),
      h('p', { className: 'hfv2-landing-sticky-note' }, svg(LANDING_ICONS['lock-muted']), 'Secure & private'));
  }

  function renderLogin() {
    var wrap = h('div', { className: 'hfv2-screen hfv2-landing-screen', id: 'hfv2-landing-hero' });

    if (CONFIG.logoUrl) {
      wrap.appendChild(h('div', { className: 'hfv2-landing-logo-bar' },
        h('img', { className: 'hfv2-landing-logo', src: CONFIG.logoUrl, alt: 'Healthfab' })));
    }

    var card = h('div', { className: 'hfv2-card hfv2-landing-card' });

    // Two columns: the headline and checklist stack on the left, the photo
    // fills the right and bleeds into the card's top-right corner (the image
    // carries its own pink backdrop, so it reads as part of the card).
    card.appendChild(h('div', { className: 'hfv2-landing-hero' },
      h('div', { className: 'hfv2-landing-hero-copy' },
        // Two lines, not one wrapping string — Figma (572:3466) sets the break
        // after "what" explicitly, and honouring it keeps the headline clear of
        // the doctor photo behind it at every width instead of running across
        // her face on wider screens where the whole line would otherwise fit.
        h('h1', { className: 'hfv2-landing-title' },
          h('span', null, "Let's listen to what"),
          h('span', null, 'your body says.')),
        h('div', { className: 'hfv2-landing-points' },
          LANDING_CHECKLIST.map(function (item) {
            var iconUrl = CONFIG.landingChecklistIcons && CONFIG.landingChecklistIcons[item.icon];
            return h('div', { className: 'hfv2-landing-point' },
              h('span', { className: 'hfv2-landing-point-icon' }, iconUrl ? h('img', { src: iconUrl, alt: '', width: 14, height: 14 }) : svg(LANDING_ICONS.check)),
              h('p', null, item.text));
          }))),
      h('div', { className: 'hfv2-landing-hero-img-wrap' },
        h('img', {
          className: 'hfv2-landing-hero-img',
          src: CONFIG.landingHeroPhotoUrl,
          alt: 'Healthfab',
        }))));

    if (!state.otpEnabled) {
      card.appendChild(h('p', { className: 'hfv2-subhead', style: { textAlign: 'center' } }, "Login is temporarily unavailable. Please check back shortly."));
      wrap.appendChild(card);
      return wrap;
    }

    if (state.loginStep === 'phone') {
      card.appendChild(h('p', { className: 'hfv2-landing-login-label' }, 'Check your Health FabScore now.'));
    }

    var err = renderErrorBanner();
    if (err) card.appendChild(err);

    if (state.loginStep === 'phone') {
      // Country code sits in its own box beside the number, per the design.
      // It's display-only — the app is India-only, so there is nothing to
      // pick, which is also why the dropdown chevron was dropped: it implied
      // a country picker that doesn't exist yet. Add it back if/when this
      // supports more than +91.
      var phoneInput = h('input', { className: 'hfv2-landing-phone-input', type: 'tel', inputmode: 'numeric', placeholder: 'Enter mobile number', id: 'hfv2-phone', value: state.loginPhone, maxlength: '10' });
      card.appendChild(h('div', { className: 'hfv2-landing-phone-row' },
        h('span', { className: 'hfv2-landing-phone-cc' }, '+91'),
        h('div', { className: 'hfv2-landing-phone-field' }, phoneInput)));
      var otpBtnActive = digitsOnly(state.loginPhone).length === 10;
      var otpBtn = h('button', {
        className: 'hfv2-cta hfv2-landing-otp-btn' + (otpBtnActive ? ' is-active' : ''), disabled: state.saving,
        onClick: function () {
          var phone = digitsOnly(phoneInput.value);
          if (phone.length !== 10) { setState({ error: 'Enter a valid 10-digit mobile number' }); return; }
          // Keep what she typed in state BEFORE the re-render: setState wipes
          // and rebuilds the DOM, and the rebuilt input reads its value from
          // state.loginPhone — so without this the number visibly disappears
          // while "Sending…" is up, and is gone entirely if the request fails.
          setState({ saving: true, error: null, loginPhone: phone });
          api.requestOtp(phone).then(function (res) {
            if (!res.ok) { setState({ saving: false, error: res.message }); return; }
            setState({ saving: false, loginStep: 'otp', loginPhone: phone, devOtp: (res.data && res.data.devOtp) || null });
            setTimeout(function () { var f = document.getElementById('hfv2-otp-0'); if (f) f.focus(); }, 30);
          });
        },
      }, state.saving ? 'Sending…' : 'Send OTP');
      // Direct DOM class toggle, not setState — setState rebuilds the whole
      // card on every call, which would drop the input's focus/cursor on
      // every keystroke. The button just needs its own look to react live.
      phoneInput.addEventListener('input', function () {
        otpBtn.classList.toggle('is-active', digitsOnly(phoneInput.value).length === 10);
      });
      card.appendChild(otpBtn);
      card.appendChild(h('p', { className: 'hfv2-landing-secure' },
        svg(LANDING_ICONS['lock-muted']), 'Secure & private'));
    } else {
      card.appendChild(h('p', { className: 'hfv2-subhead', style: { textAlign: 'center' } }, 'Enter the 6-digit code sent to WhatsApp'));
      if (state.devOtp) {
        card.appendChild(h('p', { className: 'hfv2-dev-hint' }, 'Dev mode — your code is ' + state.devOtp));
      }
      var boxes = h('div', { className: 'hfv2-otp-boxes' });
      for (var i = 0; i < 6; i++) {
        boxes.appendChild(h('input', {
          type: 'text', inputmode: 'numeric', maxlength: '1', id: 'hfv2-otp-' + i,
          onInput: (function (idx) { return function (e) { var v = digitsOnly(e.target.value).slice(0, 1); e.target.value = v; if (v) { var n = document.getElementById('hfv2-otp-' + (idx + 1)); if (n) n.focus(); } }; })(i),
          onKeydown: (function (idx) { return function (e) { if (e.key === 'Backspace' && !e.target.value && idx > 0) { var p = document.getElementById('hfv2-otp-' + (idx - 1)); if (p) p.focus(); } }; })(i),
        }));
      }
      card.appendChild(boxes);
      card.appendChild(h('button', {
        className: 'hfv2-cta', disabled: state.saving,
        onClick: function () {
          var otp = '';
          for (var i = 0; i < 6; i++) otp += (document.getElementById('hfv2-otp-' + i) || {}).value || '';
          if (otp.length !== 6) { setState({ error: 'Enter the 6-digit code' }); return; }
          setState({ saving: true, error: null });
          api.verifyOtp(state.loginPhone, otp).then(function (res) {
            if (!res.ok) { setState({ saving: false, error: res.message }); return; }
            // Force-flushed with the same anonId — this is the moment the
            // backend re-keys every pre-login event onto her phone number.
            evTrack('Identify', {}, true);
            setState({ saving: false, loggedIn: true });
            // Logging in is a landing moment like any other — the server
            // decides which screen she comes back to (her next unfinished
            // step, or her score), and the nav follows it. Without this a
            // returning customer lands on her score with the Home pill lit.
            loadScreen({ followTab: true });
          });
        },
      }, state.saving ? 'Verifying…' : 'Verify & continue'));
      card.appendChild(h('p', { className: 'hfv2-resend' }, h('button', { onClick: function () { setState({ loginStep: 'phone', error: null, devOtp: null }); } }, 'Use a different number')));
    }

    wrap.appendChild(card);
    wrap.appendChild(renderLandingWalkaway());
    wrap.appendChild(renderLandingHow());
    wrap.appendChild(renderLandingFeatures());
    wrap.appendChild(renderLandingTestimonials());
    wrap.appendChild(renderLandingFaq());
    wrap.appendChild(renderLandingStickyCta());
    return wrap;
  }

  // ─── BLOCK REGISTRY ─────────────────────────────────────────
  // FR-9: an unrecognised block type is skipped, never thrown on.
  //
  // `bottom_nav` is deliberately NOT in this map. It used to be a block the
  // server opted into per-screen, which is why it never showed up during
  // onboarding — the server just never sent it there. The nav is now a
  // persistent, client-owned tab bar (Home/Calendar/Score/Doctors/PCOS) that
  // renderResolvedScreen() appends once per screen unconditionally, so a
  // bottom_nav block arriving from the server is harmless and simply
  // ignored here rather than producing a second nav on top of the first.
  var BLOCKS = {
    header: renderHeaderBlock,
    question: renderQuestionBlock,
    cta: renderCtaBlock,
    legal: renderLegalBlock,
    checkin_prompt: renderCheckinPromptBlock,
    // wallet_badge is deliberately absent: the account button it carried is
    // now the app-wide top bar (renderTopBar), so rendering the block too
    // would put two avatars on the check-in screen. An unregistered block
    // type is skipped silently (FR-9), which is exactly what's wanted.

    score_gauge: renderScoreGaugeBlock,
    next_steps: renderNextStepsBlock,
    product_grid: renderProductGridBlock,
  };

  function screenContext(screenId) {
    if ((screenId || '').indexOf('onboarding_step_') === 0) return 'onboarding';
    if (screenId === 'checkin') return 'checkin';
    return null;
  }

  function renderResolvedScreen(payload) {
    var screen = payload.screen;
    var ctx = screenContext(screen.id);
    // .hfv2-screen reserves 96px at the bottom for the nav bar. Onboarding
    // has no nav, so that reservation would just be dead space under the
    // card — drop it there.
    var wrap = h('div', { className: 'hfv2-screen' + (ctx === 'onboarding' ? ' hfv2-screen--no-nav' : '') });

    if (ctx === 'onboarding') {
      var card = h('div', { className: 'hfv2-card' });
      card.appendChild(renderProgress(screen.stepIndex, screen.stepCount));
      var err = renderErrorBanner();
      if (err) card.appendChild(err);
      // Everything from the CTA onward — the Back/Continue row and the legal
      // line under it — moves into a sticky footer, so the way forward is
      // reachable at any scroll position instead of only at the end of a
      // long step. Whatever comes before it scrolls underneath.
      //
      // The card is also a full-height flex column (see .hfv2-screen--no-nav
      // .hfv2-card), and margin-top:auto goes on the FOOTER so the slack sits
      // between the last question and the bar. It used to go on the consent,
      // which jammed the consent against the card's bottom edge — and since
      // the bar sticks to the bottom of the VIEWPORT, which on a page with
      // browser chrome is higher than the card's bottom, the bar came down on
      // top of the consent text and clipped its second line.
      var footer = h('div', { className: 'hfv2-sticky-actions hfv2-push-down' });
      var reachedCta = false;
      (screen.blocks || []).forEach(function (block) {
        var fn = BLOCKS[block.type];
        if (!fn) return;
        var node = fn(block, screen);
        if (!node) return;
        if (block.type === 'cta') {
          reachedCta = true;
          // Back sits beside Continue rather than up by the progress bar —
          // the two navigation choices belong together, under her thumb.
          var back = renderBackButton(screen);
          if (back) node = h('div', { className: 'hfv2-actions' }, back, node);
        }
        if (reachedCta) { footer.appendChild(node); return; }
        card.appendChild(node);
      });
      if (footer.childNodes.length) card.appendChild(footer);
      wrap.appendChild(card);
      // No bottom nav while she is still answering steps 1–3. The tabs are
      // what she gets FOR finishing: onboarding_commit lands her on Score
      // and the nav appears with it. Showing it mid-assessment offered
      // exits from a flow that only pays off at the end.
      return wrap;
    }

    // Non-card screens (checkin / score) — full-bleed sections, not one boxed card.
    var frag = h('div', null);
    var err2 = renderErrorBanner();
    if (err2) frag.appendChild(h('div', { className: 'hfv2-section' }, err2));
    (screen.blocks || []).forEach(function (block) {
      var fn = BLOCKS[block.type];
      if (!fn) return;
      var node = fn(block, screen);
      if (node) frag.appendChild(node);
    });
    wrap.appendChild(frag);
    wrap.appendChild(renderBottomNavBlock());
    return wrap;
  }

  // ── Back through the onboarding steps ──
  // Keeps the onboarding payloads the server has issued, oldest first. If a
  // step comes back a second time (she cleared a required answer, so the
  // server routes her to it again) it replaces the old copy and anything
  // after it is dropped — those screens are no longer ahead of her.
  function recordOnboardingScreen(payload) {
    var screen = payload && payload.screen;
    var stack = (state.screenStack || []).slice();
    // A restored stack was drawn from an older content pack — its questions
    // may not exist any more, so it can't be replayed. Start over.
    if (storedStackVersion != null && storedStackVersion !== contentVersion) {
      stack = [];
      storedStackVersion = contentVersion;
    }
    if (!screen || screenContext(screen.id) !== 'onboarding') return stack;
    for (var i = 0; i < stack.length; i++) {
      if (stack[i].screen && stack[i].screen.id === screen.id) {
        stack.length = i;
        break;
      }
    }
    stack.push(payload);
    saveScreenStack(stack, contentVersion);
    return stack;
  }

  function cachedOnboardingScreen(stepId) {
    var stack = state.screenStack || [];
    for (var i = 0; i < stack.length; i++) {
      if (stack[i].screen && stack[i].screen.id === stepId) return stack[i];
    }
    return null;
  }

  // Step ids are onboarding_step_N, so the neighbouring step is just N±1.
  // Returns null past either end, which is what hides the Back button on
  // step 1 and keeps it off non-onboarding screens.
  function onboardingStepId(screen, delta) {
    var m = /^(onboarding_step_)(\d+)$/.exec((screen && screen.id) || '');
    if (!m) return null;
    var n = Number(m[2]) + delta;
    if (n < 1) return null;
    if (screen.stepCount && n > screen.stepCount) return null;
    return m[1] + n;
  }

  function renderBackButton(screen) {
    var prevId = onboardingStepId(screen, -1);
    if (!prevId) return null; // step 1, or not an onboarding screen
    return h('button', {
      className: 'hfv2-back', type: 'button',
      onClick: function () {
        evTrack('Button_Clicked', { id: 'back', to: prevId });
        // Asks the server for that step by name. Nothing is posted or undone
        // — her answers stay recorded and come back pre-filled from each
        // question's savedAnswer. loadScreen falls back to the copy it
        // already holds if this server build ignores stepId.
        clearFieldErrors();
        loadScreen({ stepId: prevId });
      },
    }, svg('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>'), 'Back');
  }

  function renderProgress(stepIndex, stepCount) {
    if (!stepIndex || !stepCount) return h('div');
    var wrap = h('div', null);
    wrap.appendChild(h('p', { className: 'hfv2-step-label' }, 'Step ' + stepIndex + ' of ' + stepCount));
    var bar = h('div', { className: 'hfv2-progress' });
    for (var i = 1; i <= stepCount; i++) {
      bar.appendChild(h('div', { className: 'hfv2-progress__seg', 'data-state': i < stepIndex ? 'done' : (i === stepIndex ? 'current' : 'todo') }));
    }
    wrap.appendChild(bar);
    return wrap;
  }

  function renderHeaderBlock(block, screen) {
    // Onboarding screens render this INSIDE .hfv2-card, which already owns
    // 20px of horizontal padding — no extra inset needed there. Every other
    // screen (checkin, ...) renders full-bleed with no padded ancestor, so
    // it needs its own — see the .hfv2-header rule and .hfv2-screen's
    // comment in fabscore-v2.css for why padding lives on each block instead
    // of the outer screen wrapper.
    var bare = screenContext(screen && screen.id) === 'onboarding';
    return h('div', { className: bare ? null : 'hfv2-header' },
      h('h1', { className: 'hfv2-h1' }, block.title || ''),
      block.subtitle ? h('p', { className: 'hfv2-subhead' }, block.subtitle) : null);
  }

  function renderLegalBlock(block) {
    return h('p', { className: 'hfv2-legal' }, block.text || '', block.linkText ? [' ', h('a', { href: '#', onClick: function (e) { e.preventDefault(); } }, block.linkText)] : null);
  }

  // ── Score gauge ──
  // The backend already hydrates score/gradeBand/items directly onto the
  // block (fabscoreV2ScreenService.hydrateBlock) — read from `block`, not a
  // second lookup elsewhere.
  function renderScoreGaugeBlock(block) {
    var score = block.score;
    var band = block.gradeBand;
    var w = 240, h2 = 150, cx = w / 2, cy = 130, r = 100;
    // SVG y grows downward, so subtract to bow the arc UP above cy (180°=left,
    // 90°=top, 0°=right) — a plain "+" here draws the arc as a downward U
    // that falls outside the viewBox instead of the intended dome shape.
    function pt(deg) { var rad = (deg * Math.PI) / 180; return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) }; }
    var p0 = pt(180), p1 = pt(120), p2 = pt(60), p3 = pt(0);
    function arc(a, b) { return 'M ' + a.x.toFixed(1) + ' ' + a.y.toFixed(1) + ' A ' + r + ' ' + r + ' 0 0 1 ' + b.x.toFixed(1) + ' ' + b.y.toFixed(1); }
    var pct = Math.max(0, Math.min(100, score == null ? 50 : score)) / 100;
    var dot = pt(180 - pct * 180);

    var svgEl = svg(
      '<svg class="hfv2-gauge" viewBox="0 0 ' + w + ' ' + h2 + '" role="img" aria-label="FabScore ' + (score != null ? score : '') + ' out of 100">' +
        '<path d="' + arc(p0, p1) + '" stroke="var(--hfv2-red)" stroke-width="14" fill="none" stroke-linecap="round"/>' +
        '<path d="' + arc(p1, p2) + '" stroke="var(--hfv2-amber)" stroke-width="14" fill="none" stroke-linecap="round"/>' +
        '<path d="' + arc(p2, p3) + '" stroke="var(--hfv2-green)" stroke-width="14" fill="none" stroke-linecap="round"/>' +
        '<circle cx="' + dot.x.toFixed(1) + '" cy="' + dot.y.toFixed(1) + '" r="7" fill="#12183A" stroke="#fff" stroke-width="2.5"/>' +
        '<text x="' + cx + '" y="' + (cy - 4) + '" text-anchor="middle" class="hfv2-gauge__num">' + (score != null ? score : '—') + '</text>' +
        '<text x="' + cx + '" y="' + (cy + 18) + '" text-anchor="middle" class="hfv2-gauge__label">FabScore' + (band ? ' · ' + band.label : '') + '</text>' +
      '</svg>'
    );

    return h('div', { className: 'hfv2-score-block' },
      h('h2', null, block.title || 'Your FabScore'),
      h('div', { className: 'hfv2-score-card' }, svgEl,
        h('button', { className: 'hfv2-meaning-link' }, 'ⓘ What your FabScore means')));
  }

  function renderNextStepsBlock(block) {
    var items = block.items || [];
    var section = h('div', { className: 'hfv2-section' });
    section.appendChild(h('div', { className: 'hfv2-section__head' }, h('h3', { className: 'hfv2-section__title' }, block.title || 'Your next steps')));
    var list = h('ul', { className: 'hfv2-steps' });
    items.forEach(function (item) {
      list.appendChild(h('li', { className: 'hfv2-step-row' },
        h('span', null, item.text),
        item.linkText ? h('a', { href: '#', className: 'hfv2-step-row__link', onClick: function (e) { e.preventDefault(); } }, item.linkText + ' →') : null));
    });
    section.appendChild(list);
    return section;
  }

  // ── Products — sourced from the Horizon theme itself, NOT the backend
  // (docs/fabscore/v2-spec.md ADR-9). Reads Shopify's own storefront
  // /products.json, same-origin, no auth needed. Rendered async: the block
  // shows a lightweight loading state first, then fills in once fetched. ──
  var productsCache = null;
  var PRODUCTS_PER_PAGE = 250; // Shopify's ceiling for /products.json
  var PRODUCTS_MAX_PAGES = 4;  // a stop, so a huge catalogue can't hang the screen
  var PRODUCTS_CHUNK = 12;     // cards added to the rail at a time (see renderMore)

  // Walks /products.json until a short page comes back, so the whole catalogue
  // is in the rail rather than an arbitrary first slice — she can scroll
  // sideways through all of it.
  function fetchProductPage(page) {
    return fetch('/products.json?limit=' + PRODUCTS_PER_PAGE + '&page=' + page, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .catch(function () { return { products: [] }; });
  }

  function fetchThemeProducts() {
    if (productsCache) return Promise.resolve(productsCache);
    var all = [];
    function next(page) {
      return fetchProductPage(page).then(function (json) {
        var batch = json.products || [];
        all = all.concat(batch);
        // A full page means there may be more; anything short is the end.
        if (batch.length === PRODUCTS_PER_PAGE && page < PRODUCTS_MAX_PAGES) return next(page + 1);
        return all;
      });
    }
    return next(1)
      .then(function (products) {
        productsCache = products.map(function (p) {
          var variant = (p.variants || [])[0];
          return {
            id: p.id, // Shopify's numeric product id — distinct from handle, both go on events
            handle: p.handle,
            title: p.title,
            url: '/products/' + p.handle,
            image: (p.images && p.images[0] && p.images[0].src) || null,
            variantId: variant ? variant.id : null,
            priceFormatted: variant ? variant.price : null,
            // Shopify only sets compare_at_price when it's an actual markdown;
            // guarded against it being present but not higher, which would
            // strike through a number equal to the one being charged.
            compareAt: variant && variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)
              ? variant.compare_at_price : null,
            available: variant ? !!variant.available : false,
          };
        });
        return productsCache;
      })
      .catch(function () { return []; });
  }

  // Recommendation_Viewed means "a product SHOWN in the grid". With the whole
  // catalogue in the rail, firing on render would push hundreds of events for
  // cards she never scrolls to — so each one reports itself the first time it
  // is actually on screen.
  var productSeen = null;
  function observeProductCard(card, product) {
    if (typeof IntersectionObserver !== 'function') {
      evTrack('Recommendation_Viewed', { productId: product.id, handle: product.handle });
      return;
    }
    if (!productSeen) {
      productSeen = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          productSeen.unobserve(entry.target); // once per product, not per scroll
          var p = entry.target.__hfv2Product;
          if (p) evTrack('Recommendation_Viewed', { productId: p.id, handle: p.handle });
        });
      }, { threshold: 0.5 });
    }
    card.__hfv2Product = product;
    productSeen.observe(card);
  }

  function renderProductGridBlock(block) {
    var section = h('div', { className: 'hfv2-section' });
    section.appendChild(h('div', { className: 'hfv2-section__head' },
      h('h3', { className: 'hfv2-section__title' }, block.title || 'Suggested for you'),
      h('button', { className: 'hfv2-section__link' }, 'View all →')));
    var rail = h('div', { className: 'hfv2-products' }, h('p', { className: 'hfv2-products-empty' }, 'Loading…'));
    section.appendChild(rail);

    fetchThemeProducts().then(function (products) {
      rail.innerHTML = '';
      if (!products.length) { rail.appendChild(h('p', { className: 'hfv2-products-empty' }, 'No products to show right now.')); return; }

      // The whole catalogue is available, but it is NOT all put in the DOM at
      // once — a full store's worth of cards and images makes the page
      // itself heavy enough that vertical scrolling stutters. A screenful is
      // rendered, and more is appended as she scrolls the rail towards the
      // end, so it still behaves like one long side-scrolling list.
      var rendered = 0;
      function renderMore() {
        var stop = Math.min(rendered + PRODUCTS_CHUNK, products.length);
        for (; rendered < stop; rendered++) {
          var p = products[rendered];
          var card = h('div', { className: 'hfv2-product-card' },
            p.image ? h('img', { className: 'hfv2-product-card__img', src: p.image, alt: p.title, loading: 'lazy' })
                    : h('div', { className: 'hfv2-product-card__img' }),
            h('p', { className: 'hfv2-product-card__title' }, p.title),
            p.priceFormatted ? h('p', { className: 'hfv2-product-card__price' },
              h('span', { className: 'hfv2-product-card__now' }, '₹' + p.priceFormatted),
              p.compareAt ? h('s', { className: 'hfv2-product-card__was' }, '₹' + p.compareAt) : null) : null,
            h('button', {
              className: 'hfv2-product-card__add', disabled: !p.available,
              onClick: (function (prod) { return function () { addToCart(prod); }; })(p),
            }, p.available ? 'Add' : 'Sold out'));
          rail.appendChild(card);
          observeProductCard(card, p);
        }
      }

      renderMore();
      rail.addEventListener('scroll', function () {
        if (rendered >= products.length) return;
        // Within two screens of the end — extend before she reaches the edge.
        if (rail.scrollLeft + rail.clientWidth * 2 >= rail.scrollWidth) renderMore();
      }, { passive: true });
    });

    return section;
  }

  // Journey-ref attribution (§10 of the contract): the line-item property
  // approach, since it's the preferred one AND it's the only one that also
  // records which product she added — a cart-level attribute (the
  // alternative) can't do that. No recommendationId to send in this build —
  // the product grid is sourced from the storefront, not a backend
  // recommendation with its own id, so there's nothing real to put there.
  function addToCart(product) {
    var variantId = product && product.variantId;
    if (!variantId) return;
    var ref = getFabscoreRef();
    var score = (findBlockInScreen(state.scoreScreen, 'score_gauge') || {}).score;
    evTrack('Cart_AddClicked', { productId: product.id, recommendationId: null, score: score != null ? score : null, fabscoreRef: ref });

    var payload = { id: variantId, quantity: 1 };
    if (ref) {
      payload.properties = {
        '_fabscore_ref': ref,
        '_fabscore_source': 'fabscore',
      };
    }
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) return;
      evTrack('Cart_AddSucceeded', { productId: product.id, variantId: variantId, fabscoreRef: ref });
      // The theme listens for `cart:update` (ThemeEvents.cartUpdate in
      // events.js) — cart-icon.js and component-cart-items.js both re-render
      // off it. This used to dispatch `cart:refresh`, a name nothing in the
      // theme listens for, so the drawer never learned about the add.
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: 'fabscore-v2', data: { source: 'fabscore-v2', itemCount: cart ? cart.item_count : undefined } },
          }));
          openThemeCartDrawer();
        });
    }).catch(function () { /* best-effort — a failed add shouldn't crash the score screen */ });
  }

  // Checkout happens outside this app's own DOM (the theme's shared cart
  // drawer / native checkout button, owned elsewhere) — this only OBSERVES
  // a click on a checkout link/button anywhere on the page, it doesn't
  // touch or depend on those other files. A capture-phase listener so it
  // still sees the click even if something else calls stopPropagation().
  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('a[href*="/checkout"], button[name="checkout"], [data-fabscore-checkout]');
    if (!el) return;
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
      .then(function (cart) {
        evTrack('Checkout_Started', { cartId: cart && cart.token ? cart.token : null, fabscoreRef: getFabscoreRef() }, true);
      });
  }, true);

  // ── Check-in prompt ──
  // Scratch UI state, keyed by touchpointId so a stale selection from a
  // PREVIOUS visit's touchpoint can never make today's Save button look
  // enabled before she has actually tapped anything today (the backend
  // would reject that commit anyway — no active answer row for today's
  // touchpoint — but the button falsely appearing enabled is still a real
  // UX bug worth not shipping).
  var checkinAnswer = { touchpointId: null, optionId: null };
  function renderCheckinPromptBlock(block) {
    if (!block.due || !block.question) {
      return h('div', { className: 'hfv2-checkin-idle' }, h('p', null, 'No check-in due today — come back tomorrow.'));
    }
    if (checkinAnswer.touchpointId !== block.touchpointId) checkinAnswer = { touchpointId: block.touchpointId, optionId: null };
    var q = block.question;
    var section = h('div', { className: 'hfv2-section' });
    section.appendChild(h('h1', { className: 'hfv2-h1' }, q.ask));

    var pills = h('div', { className: 'hfv2-pills' });
    (q.options || []).forEach(function (opt) {
      var pressed = checkinAnswer.optionId === opt.id;
      pills.appendChild(h('button', {
        className: 'hfv2-pill', type: 'button', 'aria-pressed': pressed ? 'true' : 'false',
        onClick: function () {
          checkinAnswer.optionId = opt.id;
          // Tracked the same way as onboarding answers (pendingAnswers) so a
          // fast tap-then-Save can't race the commit against an answer POST
          // that hasn't landed yet — see postOnboardingAnswer's comment.
          var p = api.answer({ questionId: q.id, value: opt.id, context: 'checkin', touchpointId: block.touchpointId });
          pendingAnswers.push(p);
          var remove = function () { var i = pendingAnswers.indexOf(p); if (i !== -1) pendingAnswers.splice(i, 1); };
          p.then(remove, remove);
          render();
        },
      }, opt.label));
    });
    section.appendChild(pills);

    var err = renderErrorBanner();
    if (err) section.appendChild(err);

    section.appendChild(h('button', {
      className: 'hfv2-cta', disabled: !checkinAnswer.optionId || state.saving,
      onClick: function () {
        setState({ saving: true, error: null });
        waitForPendingAnswers().then(function () {
          return api.commitCheckin();
        }).then(function (res) {
          if (!res.ok) { setState({ saving: false, error: res.message }); return; }
          checkinAnswer.optionId = null;
          setState({ saving: false });
          loadScreen({ followTab: true }); // check-in done — the server sends her on
        });
      },
    }, state.saving ? 'Saving…' : 'Save to my Health Score'));

    return section;
  }

  // Home = whatever the server's canonical /screen currently is (an
  // onboarding step, a due check-in, or the score screen once nothing else
  // is pending). Score = the cached score-bearing screen (see loadScreen()).
  // Calendar/Doctors/PCOS are static, client-only "coming soon" placeholders
  // — no fetch, no backend screen exists for them yet.
  var NAV_ITEMS = [
    { key: 'home', label: 'Home' },
    { key: 'calendar', label: 'Calendar', soon: true },
    { key: 'score', label: 'Score' },
    { key: 'doctors', label: 'Doctors', soon: true },
    { key: 'pcos', label: 'PCOS', soon: true },
  ];

  function renderBottomNavBlock() {
    var nav = h('nav', { className: 'hfv2-bottom-nav' });
    NAV_ITEMS.forEach(function (item) {
      var current = state.activeTab === item.key;
      var btn = h('button', {
        className: 'hfv2-nav-item', 'aria-current': current ? 'true' : 'false',
        onClick: function () {
          setState({ activeTab: item.key });
          // Home/Score both show server content — refresh it on every tap
          // (matches the old nav's always-refetch behaviour) rather than
          // trusting a possibly-stale cache. Coming-soon tabs fetch nothing.
          if (item.key === 'home' || item.key === 'score') loadScreen();
          else evTrack('ComingSoon_Clicked', { feature: item.key });
        },
      }, h('span', null, item.label));
      if (item.soon) btn.appendChild(h('span', { className: 'hfv2-nav-item__soon' }, 'Soon'));
      nav.appendChild(btn);
    });
    return nav;
  }

  // ── Age gate ────────────────────────────────────────────────
  // Under-18 can't continue. The server stays the authority (FR-3) — this is
  // only so she finds out on the question itself rather than after filling in
  // a whole step that then gets rejected.
  //
  // The age question comes from the backend like every other one, so it is
  // matched on its id/key/ask text rather than a hardcoded question id, and
  // both shapes it can arrive in are handled: a date of birth, or a select
  // whose options are age bands. Anything it can't read is left alone — this
  // never blocks on a shape it doesn't understand.
  var AGE_MIN = 18;
  var AGE_GATE_NOTE_ID = 'hfv2-age-gate-note';

  function looksLikeAgeQuestion(q) {
    var hay = ((q.id || '') + ' ' + (q.key || '') + ' ' + (q.ask || '')).toLowerCase();
    return /(^|[^a-z])age([^a-z]|$)|birth|dob/.test(hay);
  }

  // true = under age, false = fine, null = couldn't tell (never blocks).
  function isUnderAge(q, raw) {
    if (raw == null || raw === '') return null;

    if (q.type === 'date') {
      var d = new Date(String(raw) + 'T00:00:00');
      if (isNaN(d.getTime())) return null;
      var now = new Date();
      var years = now.getFullYear() - d.getFullYear();
      var m = now.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
      return years < AGE_MIN;
    }

    var label = String(raw).toLowerCase();
    if (/under\s*18|below\s*18|less than\s*18|<\s*18/.test(label)) return true;
    // First number in the band is its floor — "16-17" blocks, "18-22" doesn't.
    var nums = label.match(/\d+/g);
    if (!nums) return null;
    return parseInt(nums[0], 10) < AGE_MIN;
  }

  // Reads whatever is currently answered straight off the DOM — answers live
  // there and in closures, not in `state`, because selecting an option
  // deliberately does not re-render.
  function isScreenAgeBlocked(screen) {
    if (!screen) return false;
    var blocked = false;
    (screen.blocks || []).forEach(function (block) {
      if (block.type !== 'question' || !block.question) return;
      var q = block.question;
      if (!looksLikeAgeQuestion(q)) return;
      var el = document.getElementById('hfv2-q-' + q.id);
      if (!el) return;
      // Selects expose the chosen option's label; inputs expose their value.
      var raw = el.ageLabel != null && el.ageLabel !== '' ? el.ageLabel : el.value;
      if (isUnderAge(q, raw) === true) blocked = true;
    });
    return blocked;
  }

  // Toggles the CTA in place — both the age gate AND "every required
  // question answered yet". Called from the answer handlers rather than
  // through setState, because a re-render here would wipe every answer
  // already filled in on the screen. Named for what it now does; keeps the
  // one DOM node (and the age-gate note) as the single source of truth for
  // "can she actually press Continue right now" rather than only checking
  // on click, so the button visibly turns on the moment the last required
  // field is filled instead of staying colored the whole time.
  function refreshCtaGate() {
    var screen = state.screen && state.screen.screen;
    var cta = document.getElementById('hfv2-cta-primary');
    if (!screen || !cta) return;

    var blocked = isScreenAgeBlocked(screen);
    var missing = requiredQuestionsSatisfied(screen);
    // Under-age is a hard stop, so that one really is `disabled`. An
    // unfinished screen is NOT: the button has to stay clickable so a tap
    // can carry her to the field she skipped and say what's needed there.
    // It only *looks* inactive — see .hfv2-cta[data-incomplete="true"].
    cta.disabled = blocked || state.saving;
    if (missing.length) cta.setAttribute('data-incomplete', 'true');
    else cta.removeAttribute('data-incomplete');
    var note = document.getElementById(AGE_GATE_NOTE_ID);
    if (blocked && !note) {
      note = h('p', { className: 'hfv2-age-gate-note', id: AGE_GATE_NOTE_ID },
        'FabScore is for 18 and over.');
      if (cta.parentNode) cta.parentNode.insertBefore(note, cta.nextSibling);
    } else if (!blocked && note && note.parentNode) {
      note.parentNode.removeChild(note);
    }
  }

  // ── CTA (onboarding_next / onboarding_commit) ──
  function requiredQuestionsSatisfied(screen) {
    // Light client-side check so tapping Continue with an empty required
    // field gives immediate feedback instead of a round trip that visibly
    // does nothing (the server is still the actual authority — see FR-3 —
    // this only pre-empts an obviously-incomplete submit).
    var missing = [];
    (screen.blocks || []).forEach(function (block) {
      if (block.type !== 'question' || !block.question) return;
      var q = block.question;
      // A multi-select always needs at least one chip, whatever the content
      // pack says. The seeded questions (Q_PERIOD_PRODUCTS, Q_CONCERNS,
      // Q_SUPPLEMENTS) are required:false, which is why Continue used to
      // sail past them untouched. Note this only holds while the page is
      // open: the server's own routing still reads the pack's `required`,
      // so a reload would let an empty one through — flipping those three to
      // required:true in contentSeedV1.js is what makes it stick.
      var needsOne = q.type === 'multi_select' || q.type === 'multi_select_other';
      if (!q.required && !needsOne) return;
      var el = document.getElementById('hfv2-q-' + q.id);
      var hasValue, message;
      if (q.type === 'consent') {
        hasValue = el && el.checked;
        message = 'Please tick this to continue.';
      } else if (q.type === 'period_calendar') {
        var need = (q.config && q.config.periodsToCollect) || 2;
        var have = state.calPeriods.filter(periodIsComplete).length;
        hasValue = have >= need;
        // More than the minimum is welcome — every complete period is sent.
        message = have === 0
          ? 'Please pick the start and end of a period.'
          : 'One more month, please — we need at least ' + need + ' consecutive months to work out your cycle. ' + have + ' added so far.';
      } else if (q.type === 'multi_select' || q.type === 'multi_select_other') {
        if (el && el.otherPending) {
          hasValue = false;
          message = 'Tell us what “Other” is, or unpick it.';
        } else {
          hasValue = el && el.value && el.value.trim();
          message = 'Please pick at least one.';
        }
      } else {
        hasValue = el && el.value && el.value.trim();
        message = 'Please fill this detail.';
      }
      if (!hasValue) missing.push({ ask: q.ask || q.id, el: el, message: message });
    });
    return missing;
  }

  // Takes her to the first thing she skipped and says what's wanted there,
  // instead of a banner at the top of a screen she may be scrolled well past.
  var FIELD_ERROR_CLASS = 'hfv2-field-error';
  function clearFieldErrors() {
    var nodes = document.querySelectorAll('.' + FIELD_ERROR_CLASS);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
  }
  function showMissingField(entry) {
    clearFieldErrors();
    if (!entry || !entry.el) return;
    // The message belongs under the whole field, not the bare control
    // inside it — and for consent that's the label, whose checkbox is a 0x0
    // visually-hidden input that nothing could be usefully anchored to.
    var host = entry.el.closest
      ? (entry.el.closest('.hfv2-field-group, .hfv2-consent') || entry.el)
      : entry.el;
    var note = h('p', { className: FIELD_ERROR_CLASS }, entry.message);
    if (host.parentNode) host.parentNode.insertBefore(note, host.nextSibling);
    host.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function renderCtaBlock(block, screen) {
    // Starts disabled (state.saving is false pre-interaction, so this is
    // effectively "enabled" on creation) — render() calls refreshCtaGate()
    // right after this node is actually attached to the document, which is
    // the earliest point document.getElementById() can see its sibling
    // question fields to know whether they're all filled in yet. That one
    // post-attach call is what actually greys this out on a fresh screen,
    // or leaves it lit up when Back brings her to an already-answered one.
    return h('button', {
      // Stable id so refreshCtaGate() can toggle this without a re-render.
      className: 'hfv2-cta', id: 'hfv2-cta-primary', disabled: state.saving,
      onClick: function () {
        // The button is already disabled when under age — this is the guard
        // for the case where it somehow gets clicked anyway.
        if (isScreenAgeBlocked(screen)) { refreshCtaGate(); return; }
        if (block.action === 'onboarding_next') {
          var missing = requiredQuestionsSatisfied(screen);
          if (missing.length) { showMissingField(missing[0]); return; }
          clearFieldErrors();
          setState({ saving: true, error: null });
          waitForPendingAnswers().then(function () {
            // Fired before the re-fetch, per the contract's own ordering.
            evTrack('Onboarding_StepCompleted', { stepId: screen.id, stepIndex: screen.stepIndex });
            setState({ saving: false });
            loadScreen();
          }, function () {
            // An answer write failed outright (dropped connection, server
            // restarted). Without this the promise never settles our way,
            // `saving` stays true, and refreshCtaGate keeps the button
            // disabled for the rest of the session — a dead Continue with no
            // explanation. Let her try again instead.
            setState({ saving: false, error: "Couldn't save your answers. Please try again." });
          });
          return;
        }
        if (block.action === 'onboarding_commit') {
          var missing2 = requiredQuestionsSatisfied(screen);
          if (missing2.length) { showMissingField(missing2[0]); return; }
          clearFieldErrors();
          var params = new URLSearchParams(window.location.search);
          setState({ saving: true, error: null });
          waitForPendingAnswers().then(function () {
            return api.commitOnboarding({ entryProduct: params.get('product') || null, entryRef: getEntryRef() });
          }).then(function (res) {
            if (!res.ok) { setState({ saving: false, error: res.message }); return; }
            // Store it now, not only once the follow-up GET /screen resolves
            // — the very next thing that can happen is an add-to-cart, which
            // needs this ref to exist already.
            if (res.data && res.data.fabscoreRef) setFabscoreRef(res.data.fabscoreRef);
            setState({ saving: false });
            // The assessment just finished — land wherever the server now
            // says she belongs, which is the score screen.
            loadScreen({ followTab: true });
          }, function () {
            // Same reason as onboarding_next: a rejected write must not leave
            // `saving` true, or the button stays disabled for good.
            setState({ saving: false, error: "Couldn't submit. Please try again." });
          });
          return;
        }
      },
    }, state.saving ? 'Saving…' : (block.label || 'Continue'));
  }

  // ── Question dispatch ──
  // Text/date fields post on blur, fire-and-forget, so typing an answer and
  // immediately tapping Continue can otherwise race GET /screen against a
  // POST /answer that hasn't landed yet. Every in-flight answer POST is
  // tracked here; the CTA handlers await waitForPendingAnswers() before
  // calling loadScreen()/commitOnboarding() so the server always sees what
  // was just typed.
  var pendingAnswers = [];
  // One in-flight write per question, in the order she made the changes.
  // Multi-select posts the WHOLE current set on every tap, so two quick taps
  // fire two overlapping writes — and the server supersedes whatever isn't in
  // the set it happens to process last. Unserialised, the earlier request
  // landing second silently removes the option she just added. Chaining per
  // questionId makes her last tap the one that wins.
  var answerChains = {}; // questionId -> promise tail; cleared on logout
  function postOnboardingAnswer(question, value, otherText) {
    var send = function () {
      return api.answer({ questionId: question.id, value: value, otherText: otherText, context: 'onboarding' });
    };
    var prev = answerChains[question.id] || Promise.resolve();
    // Same handler either way: a failed earlier write must not strand the
    // queue and swallow every later answer to that question.
    var p = prev.then(send, send);
    answerChains[question.id] = p.catch(function () { });
    pendingAnswers.push(p);
    var remove = function () { var i = pendingAnswers.indexOf(p); if (i !== -1) pendingAnswers.splice(i, 1); };
    p.then(remove, remove);
    return p;
  }
  function waitForPendingAnswers() { return Promise.all(pendingAnswers); }

  // `savedAnswer` (§8 of the contract) rides on the block, one level up from
  // `question` — { optionIds, freeText, otherText, structuredValue, status }.
  // Passed through to every renderer so a question that already has an
  // answer (Back-and-forward, or any re-render that rebuilt this screen from
  // a fresh GET /screen) shows what she already said instead of a blank
  // field. null whenever she hasn't answered it yet.
  function renderQuestionBlock(block) {
    var q = block.question;
    if (!q) return null;
    var saved = block.savedAnswer || null;
    switch (q.type) {
      case 'text': return renderTextQuestion(q, saved);
      case 'date': return renderDateQuestion(q, saved);
      case 'number': return renderNumberQuestion(q, saved);
      case 'scale': return renderScaleQuestion(q, saved);
      case 'consent': return renderConsentQuestion(q, saved);
      case 'single_select': return renderSingleSelectQuestion(q, saved);
      case 'multi_select': return renderMultiSelectQuestion(q, false, saved);
      case 'multi_select_other': return renderMultiSelectQuestion(q, true, saved);
      case 'period_calendar': return renderPeriodCalendarQuestion(q, saved);
      default: return null; // FR-9 — an unrecognised question type is skipped, not thrown on
    }
  }

  // Her name and DOB are only ever typed here, and the /screen envelope
  // doesn't carry them back, so the profile panel is fed from the answers as
  // she gives them. Matched on the question's own wording — the same
  // approach the age gate already takes — rather than on content-pack ids,
  // which the theme is not supposed to know.
  function looksLikeNameQuestion(q) {
    return /(^|[^a-z])name([^a-z]|$)/.test(((q.id || '') + ' ' + (q.ask || '')).toLowerCase());
  }

  function renderTextQuestion(q, saved) {
    var input = h('input', { className: 'hfv2-input', id: 'hfv2-q-' + q.id, type: 'text', placeholder: q.placeholder || '', value: (saved && saved.freeText) || '' });
    if (looksLikeNameQuestion(q) && saved && saved.freeText) cacheProfile({ name: saved.freeText });
    input.addEventListener('blur', function () {
      if (input.value.trim()) postOnboardingAnswer(q, input.value.trim());
      if (looksLikeNameQuestion(q) && input.value.trim()) cacheProfile({ name: input.value.trim() });
      refreshCtaGate();
    });
    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label', for: input.id }, q.ask), input);
  }

  function renderDateQuestion(q, saved) {
    var input = h('input', { className: 'hfv2-input', id: 'hfv2-q-' + q.id, type: 'date', max: new Date().toISOString().slice(0, 10), value: (saved && saved.freeText) || '' });
    if (looksLikeAgeQuestion(q) && saved && saved.freeText) cacheProfile({ dob: saved.freeText });
    input.addEventListener('change', function () {
      if (input.value) postOnboardingAnswer(q, input.value);
      if (looksLikeAgeQuestion(q) && input.value) cacheProfile({ dob: input.value });
      refreshCtaGate();
    });
    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label', for: input.id }, q.ask),
      h('div', { className: 'hfv2-input-wrap' }, input,
        h('span', { className: 'hfv2-input-icon' }, svg('<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/></svg>'))));
  }

  // number — a plain numeric field. Posts the value as an actual Number, per
  // the contract's "29 — number", not the string the input gives back.
  function renderNumberQuestion(q, saved) {
    var input = h('input', { className: 'hfv2-input', id: 'hfv2-q-' + q.id, type: 'number', placeholder: q.placeholder || '', value: (saved && saved.freeText != null) ? saved.freeText : '' });
    input.addEventListener('blur', function () {
      if (input.value !== '') postOnboardingAnswer(q, Number(input.value));
      refreshCtaGate();
    });
    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label', for: input.id }, q.ask), input);
  }

  // scale — a fixed 1–5 rating. The contract hydrates nothing beyond the
  // ask text for this type ("Extra fields on question: —"), so 1–5 is drawn
  // client-side; that's the widget's shape, not question content.
  function renderScaleQuestion(q, saved) {
    var selected = { value: saved && saved.freeText != null ? Number(saved.freeText) : null };
    var pillsWrap = h('div', { className: 'hfv2-pills', id: 'hfv2-q-' + q.id });
    Object.defineProperty(pillsWrap, 'value', { get: function () { return selected.value != null ? String(selected.value) : ''; } });
    for (var n = 1; n <= 5; n++) {
      (function (val) {
        pillsWrap.appendChild(h('button', {
          className: 'hfv2-pill', type: 'button', 'aria-pressed': selected.value === val ? 'true' : 'false',
          onClick: function (e) {
            selected.value = val;
            Array.prototype.forEach.call(pillsWrap.children, function (btn) { btn.setAttribute('aria-pressed', btn === e.currentTarget ? 'true' : 'false'); });
            postOnboardingAnswer(q, val);
            refreshCtaGate();
          },
        }, String(val)));
      })(n);
    }
    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label' }, q.ask), pillsWrap);
  }

  function renderConsentQuestion(q, saved) {
    // The contract's savedAnswer example only shows freeText:null for this
    // shape, without pinning down whether a real answer comes back as a
    // boolean or its string form — accept either rather than assuming one.
    var checked = !!(saved && (saved.freeText === true || saved.freeText === 'true'));
    var input = h('input', { type: 'checkbox', id: 'hfv2-q-' + q.id, checked: checked });
    input.addEventListener('change', function () { postOnboardingAnswer(q, input.checked); refreshCtaGate(); });
    return h('label', { className: 'hfv2-consent', for: input.id }, input,
      h('span', { className: 'hfv2-consent__box' }, svg('<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>')),
      q.ask);
  }

  function renderSingleSelectQuestion(q, saved) {
    var savedId = saved && saved.optionIds && saved.optionIds[0];
    var savedOpt = savedId && (q.options || []).filter(function (o) { return o.id === savedId; })[0];
    var selected = { id: savedOpt ? savedOpt.id : null, label: savedOpt ? savedOpt.label : null };
    // requiredQuestionsSatisfied() reads this element's `.value` by id — a
    // plain <div> has no such property natively, so it's defined here rather
    // than adding a separate hidden input just to hold one. `ageLabel` is the
    // same idea for refreshCtaGate(): option ids are opaque, so the gate needs
    // the human label ("16-17") to read an age band off it.
    var pillsWrap = h('div', { className: 'hfv2-pills', id: 'hfv2-q-' + q.id });
    Object.defineProperty(pillsWrap, 'value', { get: function () { return selected.id || ''; } });
    Object.defineProperty(pillsWrap, 'ageLabel', { get: function () { return selected.label || ''; } });
    (q.options || []).forEach(function (opt) {
      pillsWrap.appendChild(h('button', {
        className: 'hfv2-pill', type: 'button', 'aria-pressed': opt.id === selected.id ? 'true' : 'false',
        onClick: function (e) {
          selected.id = opt.id;
          selected.label = opt.label;
          Array.prototype.forEach.call(pillsWrap.children, function (btn) { btn.setAttribute('aria-pressed', btn === e.currentTarget ? 'true' : 'false'); });
          postOnboardingAnswer(q, opt.id);
          refreshCtaGate();
        },
      }, opt.label));
    });
    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label' }, q.ask), pillsWrap);
  }

  function renderMultiSelectQuestion(q, allowOther, saved) {
    var savedIds = (saved && saved.optionIds) || [];
    var selected = {}; // optionId -> true
    savedIds.forEach(function (id) { selected[id] = true; });
    var otherInputEl = null;
    var otherOpt = allowOther ? (q.options || []).filter(function (o) { return o.isOther; })[0] : null;

    function currentIds() { return Object.keys(selected).filter(function (k) { return selected[k]; }); }
    function sync() {
      var otherText = otherOpt && otherInputEl ? otherInputEl.value.trim() : undefined;
      postOnboardingAnswer(q, currentIds(), otherText || undefined);
      refreshCtaGate();
    }

    var pillsWrap = h('div', { className: 'hfv2-pills', id: 'hfv2-q-' + q.id });
    Object.defineProperty(pillsWrap, 'value', { get: function () { return currentIds().length ? 'x' : ''; } });
    // "Other" with an empty box says nothing — the chip is a promise to name
    // the thing. requiredQuestionsSatisfied() reads this to hold Continue
    // until she either types it or unticks the chip.
    Object.defineProperty(pillsWrap, 'otherPending', {
      get: function () {
        if (!otherOpt || !selected[otherOpt.id]) return false;
        return !(otherInputEl && otherInputEl.value.trim());
      },
    });
    var otherFieldWrap = h('div', { className: 'hfv2-other-input', hidden: !(otherOpt && selected[otherOpt.id]) });

    (q.options || []).forEach(function (opt) {
      pillsWrap.appendChild(h('button', {
        className: 'hfv2-pill', type: 'button', 'aria-pressed': selected[opt.id] ? 'true' : 'false',
        onClick: function (e) {
          selected[opt.id] = !selected[opt.id];
          e.currentTarget.setAttribute('aria-pressed', selected[opt.id] ? 'true' : 'false');
          if (opt.isOther) {
            otherFieldWrap.hidden = !selected[opt.id];
            if (selected[opt.id] && otherInputEl) otherInputEl.focus();
          }
          sync();
        },
      }, opt.label));
    });

    if (allowOther) {
      otherInputEl = h('input', {
        className: 'hfv2-input', type: 'text', id: 'hfv2-q-' + q.id + '-other',
        placeholder: q.otherPlaceholder || 'Other', value: (saved && saved.otherText) || '',
      });
      otherInputEl.addEventListener('blur', sync);
      // The answer itself is only posted on blur, but the gate has to react
      // while she types — otherwise Continue stays greyed until she taps away.
      otherInputEl.addEventListener('input', function () { clearFieldErrors(); refreshCtaGate(); });
      otherFieldWrap.appendChild(otherInputEl);
    }

    return h('div', { className: 'hfv2-field-group' }, h('label', { className: 'hfv2-label' }, q.ask), pillsWrap, allowOther ? otherFieldWrap : null);
  }

  // ── Period calendar — "when were your last two periods?" ──
  // One month on screen, arrows to move between months. It opens on the
  // current month, because that's where her most recent period is, and she
  // walks backwards from there.
  //
  // Two taps record a period: the first sets the start, the second the end.
  // Between them the five days after the start are drawn as dotted outlines
  // — a period is roughly that long, so the dots read as "and now pick the
  // day it stopped" without any wording having to explain it.
  //
  // At least periodsToCollect periods are needed (two, so a cycle length can
  // be derived from the gap between two starts), but MORE is welcome and all
  // of them are sent. A period may run across a month boundary; nothing here
  // is keyed by month, it's just a list of ranges.
  //
  // Only { periods: [{start,end}, …] } goes to the server, which derives
  // lastPeriod/periodDays/cycleLength (fabscoreV2CycleMath.js). None of those
  // derived numbers are shown here.
  var PERIOD_PREVIEW_DAYS = 5;   // dotted hint after a start is tapped
  var CAL_MAX_MONTHS_BACK = 12;  // how far the ‹ arrow may travel

  function isoOf(date) { return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0'); }
  function isoAddDays(iso, days) {
    var p = iso.split('-');
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]) + days);
    return isoOf(d);
  }
  function periodIsComplete(p) { return !!(p && p.start && p.end); }
  function inRange(dateIso, p) {
    return periodIsComplete(p) && dateIso >= p.start && dateIso <= p.end;
  }
  function prettyDate(iso) {
    var parts = iso.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  function renderPeriodCalendarQuestion(q, saved) {
    // Carries the question id so requiredQuestionsSatisfied() can find this
    // group and scroll to it when she hasn't given enough months yet.
    var periodsToCollect = (q.config && q.config.periodsToCollect) || 2;

    var wrap = h('div', { className: 'hfv2-field-group', id: 'hfv2-q-' + q.id });

    // What to do, behind an ⓘ rather than as standing text: she only needs
    // it once, and a permanent paragraph above a calendar this tall pushes
    // the grid itself off the screen. The content pack's own `help` wins
    // when it has one — wording is the server's, not ours.
    // Two parts: whatever the content pack says about tapping (its wording,
    // not ours), then what the widget itself knows — how many months are
    // needed and why. The second part is derived from periodsToCollect, so
    // it stays right even if that number changes.
    var help = h('div', {
      className: 'hfv2-cal__help', id: 'hfv2-help-' + q.id, hidden: true,
    },
      h('p', { className: 'hfv2-cal__help-title' }, 'What to do'),
      h('ol', { className: 'hfv2-cal__help-steps' },
        h('li', null, q.help || 'Tap the day your period started, then the day it ended.'),
        h('li', null, 'Use the ‹ arrow to go back a month, and do the same there.')),
      h('p', { className: 'hfv2-cal__help-note' },
        'You need ' + periodsToCollect + ' months in a row — August and September, say, not August and October. '
        + 'The gap between the two start dates is what gives us your cycle length, so one month on its own isn’t enough.'));

    var helpBtn = h('button', {
      className: 'hfv2-info', type: 'button',
      'aria-label': 'How to fill this in', 'aria-expanded': 'false', 'aria-controls': help.id,
      onClick: function () {
        help.hidden = !help.hidden;
        helpBtn.setAttribute('aria-expanded', help.hidden ? 'false' : 'true');
      },
    }, svg('<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-5M12 8h.01"/></svg>'));

    wrap.appendChild(h('div', { className: 'hfv2-label-row' },
      h('span', { className: 'hfv2-label' }, q.ask), helpBtn));
    wrap.appendChild(help);

    var box = h('div');
    wrap.appendChild(box);

    if (!Array.isArray(state.calPeriods)) state.calPeriods = [];
    // Pre-fill from what she already answered, unless something is recorded
    // locally — that would clobber an answer in progress if this question
    // re-rendered for an unrelated reason (an error banner from a different
    // question on the same step, say).
    if (!state.calPeriods.length && !state.calPendingStart
      && saved && saved.structuredValue && saved.structuredValue.periods) {
      state.calPeriods = saved.structuredValue.periods.filter(periodIsComplete);
    }

    var today = new Date();
    var todayIso = isoOf(today);

    function completePeriods() { return state.calPeriods.filter(periodIsComplete); }

    // Posted on EVERY change, not only once the minimum is reached. Removing
    // a period used to post nothing, which left the server holding periods
    // she had already deleted — so a reload would route her past this step
    // on stale data. Sending the current set every time keeps the server's
    // copy and what she sees the same thing. All complete periods go, not
    // just the first two: extra months are welcome.
    function postPeriods() {
      postOnboardingAnswer(q, { periods: completePeriods() });
    }

    function periodCovering(dIso) {
      for (var i = 0; i < state.calPeriods.length; i++) {
        if (inRange(dIso, state.calPeriods[i])) return i;
      }
      return -1;
    }

    function onDayTap(dIso) {
      if (periodCovering(dIso) !== -1) return; // already inside a recorded period
      if (!state.calPendingStart) {
        state.calPendingStart = dIso;
      } else {
        var a = state.calPendingStart;
        // She taps the start first, but keep the pair in order in case the
        // second tap lands earlier.
        var period = a <= dIso ? { start: a, end: dIso } : { start: dIso, end: a };
        state.calPeriods.push(period);
        state.calPeriods.sort(function (x, y) { return x.start < y.start ? -1 : 1; });
        state.calPendingStart = null;
        postPeriods();
      }
      redraw();
      clearFieldErrors();
      refreshCtaGate();
    }

    function removePeriod(index) {
      state.calPeriods.splice(index, 1);
      state.calPendingStart = null;
      postPeriods(); // tell the server it's gone, or it keeps the old set
      redraw();
      refreshCtaGate();
    }

    function shiftMonth(delta) {
      var next = state.calMonthOffset + delta;
      if (next < 0 || next > CAL_MAX_MONTHS_BACK) return; // no future, no endless past
      state.calMonthOffset = next;
      redraw();
    }

    function redraw() {
      box.innerHTML = '';
      box.appendChild(buildMonth());
      box.appendChild(buildRecap());
    }

    function buildMonth() {
      var offset = state.calMonthOffset;
      var viewDate = new Date(today.getFullYear(), today.getMonth() - offset, 1);
      var monthName = viewDate.toLocaleDateString('en-GB', { month: 'long' });
      var cal = h('div', { className: 'hfv2-cal' });

      cal.appendChild(h('div', { className: 'hfv2-cal__head' },
        h('button', {
          className: 'hfv2-cal__nav', type: 'button', 'aria-label': 'Previous month',
          disabled: offset >= CAL_MAX_MONTHS_BACK,
          onClick: function () { shiftMonth(1); },
        }, svg('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>')),
        h('span', { className: 'hfv2-cal__title' },
          viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })),
        h('button', {
          className: 'hfv2-cal__nav', type: 'button', 'aria-label': 'Next month',
          disabled: offset === 0, // the current month is as far forward as it goes
          onClick: function () { shiftMonth(-1); },
        }, svg('<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>'))));

      var grid = h('div', { className: 'hfv2-cal__grid' });
      ['S', 'M', 'T', 'W', 'T', 'F', 'S'].forEach(function (d) {
        grid.appendChild(h('div', { className: 'hfv2-cal__dow' }, d));
      });
      for (var pad = 0; pad < viewDate.getDay(); pad++) grid.appendChild(h('div'));

      // The five days after a tapped start, drawn dotted — the nudge that
      // an end date is what's wanted next.
      var previewFrom = state.calPendingStart;
      var previewTo = previewFrom ? isoAddDays(previewFrom, PERIOD_PREVIEW_DAYS) : null;

      var daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
      for (var d = 1; d <= daysInMonth; d++) {
        var dIso = isoOf(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
        var future = dIso > todayIso; // a period can't have run through a day that hasn't come
        var pending = dIso === state.calPendingStart;
        var covering = periodCovering(dIso);
        var period = covering === -1 ? null : state.calPeriods[covering];
        var isEndpoint = period && (period.start === dIso || period.end === dIso);
        var isPreview = !!previewFrom && !future && dIso > previewFrom && dIso <= previewTo && covering === -1;

        grid.appendChild(h('button', {
          className: 'hfv2-cal__day', type: 'button', disabled: future,
          'data-today': dIso === todayIso ? 'true' : 'false',
          'data-pending': pending ? 'true' : 'false',
          'data-endpoint': (isEndpoint && !pending) ? 'true' : 'false',
          'data-in-range': (period && !isEndpoint) ? 'true' : 'false',
          'data-preview': isPreview ? 'true' : 'false',
          onClick: future ? null : (function (iso) { return function () { onDayTap(iso); }; })(dIso),
        }, String(d)));
      }
      cal.appendChild(grid);

      var have = completePeriods().length;
      cal.appendChild(h('p', { className: 'hfv2-cal__hint' },
        state.calPendingStart
          ? 'Now tap the day it ended.'
          : have < periodsToCollect
            ? 'Tap the day a period started in ' + monthName + ' — use the arrows for earlier months.'
            : 'Added ' + have + '. Add more months if you like, or continue.'));

      return cal;
    }

    // Every period recorded so far, each removable on its own ✕.
    function buildRecap() {
      var recap = h('div', { className: 'hfv2-cal__recap' });
      state.calPeriods.forEach(function (period, index) {
        if (!periodIsComplete(period)) return;
        recap.appendChild(h('div', { className: 'hfv2-cal__recap-row' },
          h('span', { className: 'hfv2-cal__recap-label' }, 'Period ' + (index + 1)),
          h('span', { className: 'hfv2-cal__recap-range' },
            prettyDate(period.start) + ' → ' + prettyDate(period.end)),
          h('button', {
            type: 'button', className: 'hfv2-cal__recap-clear',
            'aria-label': 'Remove this period',
            onClick: (function (i) { return function () { removePeriod(i); }; })(index),
          }, '✕')));
      });
      return recap;
    }

    redraw();
    return wrap;
  }

  // ─── ROUTER ──────────────────────────────────────────────────
  function screenHasScoreGauge(screen) {
    return !!(screen && (screen.blocks || []).some(function (b) { return b.type === 'score_gauge'; }));
  }

  // Which tab a screen belongs under. The server never names a tab — it
  // names a SCREEN, and the mapping from screen to tab is 1:1, so there is
  // nothing here for it to decide separately. Onboarding returns null
  // because no nav is drawn during it.
  function tabForScreen(screen) {
    var id = screen && screen.id;
    if (!id || screenContext(id) === 'onboarding') return null;
    if (id === 'score' || screenHasScoreGauge(screen)) return 'score';
    return 'home'; // check-in, and anything else the server introduces later
  }

  // `opts.followTab` moves the nav to wherever the screen the server just
  // handed back belongs. Passed on the three occasions the server is
  // choosing where she lands: opening the app, finishing onboarding, and
  // saving a check-in. NOT passed on a nav tap — she picked that tab
  // herself, and re-deciding it under her would make Home unreachable
  // whenever the server's current screen is the score.
  function loadScreen(opts) {
    opts = opts || {};
    setState({ error: null });
    api.getScreen(opts.stepId).then(function (res) {
      if (!res.ok) {
        if (res.status === 401) { resetToLoggedOut(); return; }
        setState({ error: res.message });
        return;
      }
      // Not part of `state` — the content-pack version and journey ref are
      // read straight off every /screen response per the contract ("on
      // every GET /screen → fabscoreRef, once she has a profile"), kept in
      // their own persisted slots rather than duplicated into render state.
      if (res.data && res.data.contentVersion) contentVersion = res.data.contentVersion;
      if (res.data && res.data.fabscoreRef) setFabscoreRef(res.data.fabscoreRef);

      // If we asked for a specific step and got a different one, this build
      // of the server doesn't honour stepId — fall back to the copy of that
      // screen it handed us earlier this session, so Back still works.
      var payload = res.data;
      if (opts.stepId && payload && payload.screen && payload.screen.id !== opts.stepId) {
        var cached = cachedOnboardingScreen(opts.stepId);
        if (cached) payload = cached;
      }
      var patch = { screen: payload, screenStack: recordOnboardingScreen(res.data) };
      var hasScore = res.data && screenHasScoreGauge(res.data.screen);
      if (hasScore) patch.scoreScreen = res.data;
      if (opts.followTab) {
        var tab = tabForScreen(res.data && res.data.screen);
        if (tab) patch.activeTab = tab;
      }
      setState(patch);
    });
  }

  // ─── INIT ───────────────────────────────────────────────────
  function init() {
    trackSessionStarted();
    refreshCartCount();
    setState({ booted: false });
    api.getSession().then(function (res) {
      if (res.ok && res.data) {
        setState({ booted: true, loggedIn: true });
        // She's coming back — the server decides which screen she lands on,
        // and the nav follows it rather than sitting on its default.
        loadScreen({ followTab: true });
        return;
      }
      // Not logged in — check whether OTP login is even enabled (an ops
      // kill switch, see fabscoreV2AuthController.js) before showing the
      // phone form, rather than letting her fill it in only to have every
      // "Send code" tap fail with a generic error.
      api.getConfig().then(function (cfgRes) {
        var otpEnabled = !(cfgRes.ok && cfgRes.data && cfgRes.data.methods && cfgRes.data.methods.otp === false);
        setState({ booted: true, loggedIn: false, otpEnabled: otpEnabled });
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
